import * as React from "react";
import { ValueOf } from "lib/ts-utility";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions } from "constants/sentry-metrics";
import { $, media, merge } from "glamor";
import {
    dimensionFiltersToAgGridFilters,
    attributeFiltersToAgGridFilters,
    ProductCollectionGridView,
    ProductCollectionTileView,
} from "./product-collection-views";
import { ProductCollectionToolbar } from "./product-collection-toolbar";
import { ProductCollectionBulkActions } from "./product-collection-bulk-actions";
import * as PropTypes from "prop-types";
import { List, Map, OrderedSet, Set } from "immutable";
import memoizeOne from "memoize-one";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ASSORTMENT_COPY_ALLOCATIONS,
    ASSORTMENT_COPY_ITEMS,
    CollectionAction,
    CollectionKeys,
    ExportCollectionSelectedSetting,
    GridParamType,
    IMAGE_URL,
    SizeAllocationType,
    SizeTableTypes,
    IGridItem,
    ISizePacksTolerance,
    Item,
    ItemFields,
    Kinds,
    Panel,
    SMU_FILTER,
    TileViewSize,
    ToolPanels,
    UnitsEntryType,
    ValidationModes,
    View,
    IExportParams,
    MENU_HIDE,
} from "constants/product-collection";
import { Dialog } from "components/dialog";
import Button from "components/form/button";
import { formatMessage } from "lib/globalize-util";
import { rawTheme } from "styles/theme";
import { DATATEST } from "constants/integration";
import { ProductCollectionItemModal } from "./product-collection-views/product-collection-item-modal";
import {
    prepareDataToExportCollection,
    IDisplayFields,
    ISchemaKey,
    parseSchema,
    parseSelectedSizes,
    validateItemsSchemaRules,
    validateCollectionSchemaRules,
    getFilteredClassificationSchema,
    getFieldsByItems,
    DistributionTargets,
} from "helpers/product-collection";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionKind,
    BAG_VALUE_PARAMS_SEPARATOR,
    BagValues,
    IAllocationDimension,
    IAllocationDimensionValue,
    mutateBagValues,
} from "helpers/allocation-dimension";
import { IDeliveryPurchaseOrder, IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import {
    DOOR_GROUP_QUERY_KEY,
    getVisibleDoors,
    IDoor,
    DimensionParamsActionType,
    getDimensionFilters,
    processActionsToDimensionParams,
} from "helpers/door-collection";
import { AlertDialog } from "components/dialog/alert";
import { LinkProduct } from "./product-collection-link-product";
import uuid from "uuid/v4";
import classNames from "classnames";
import { CollectionPOI } from "records/collection/poi";
import { getRawItemDisplayProperty, IGridData } from "helpers/collection";
import { ISuggestionItem, ISuggestionObject } from "containers/product-collections/product-collection-cart";
import { ISubscription } from "reducers/subscriptions";
import _, { debounce, get, isEqual, pick } from "lodash";
import { ISizeRange } from "actions/collections/collection-sizing";
import { ManageDoorGrouping } from "./manage-door-grouping";
import { ManageDeliveryPurchaseOrder } from "./manage-delivery-purchase-order";
import {
    IGridParamsQueryParam,
    IPreference,
    ISaveStateToGridQueryParamsActions,
    saveStateToGridQueryParams,
    SaveStateToGridQueryParamsAction,
    SaveStateToGridQueryParamsType,
} from "./product-collection-views/product-collection-grid-view/tool-panels";
import {
    ICurrency,
    TotalTypes,
} from "./product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { getUpdatedSizesCurveAndStatus } from "./product-collection-item-allocation";
import { ProductDetailsModal } from "components/product-collection/product-collection-views/product-collection-detail-view";
import { IDoorTotalRow, IDoorTotals, IPackInfo } from "reducers/collection";
import { ICollectionVersionStatus } from "helpers/collection-version-status";
import { WindowAnalytics } from "helpers/window-analytics";
import { ICopyProps } from "actions/collections/collection";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { ItemKind } from "containers/folders/types/base";
import { ProductCollectionExportDialog } from "./common/product-collection-export-dialog";
import {
    ColumnTypes,
    getNewDimensionFilters,
} from "./product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { ManageTileView } from "./manage-tile-view";
import { ITileSettings } from "./manage-tile-view/types";
import { amphibiousGetIn, setIn } from "helpers/objUtils";
import { CUSTOM_VIEW_SETTINGS } from "./manage-tile-view/constants";
import { RollupType } from "containers/folders/types/server-rollups";
import { buildServerRollupRowNodeId, ISnapshotItem } from "helpers/server-rollup";
import { IImageCellDimensions } from "constants/types";
import { ProductGenericToaster } from "./product-collection-views/product-generic-toaster";
import { ProductCollectionExportToMerchantModal } from "./product-collection-export-to-merchant/product-collection-export-to-merchant-modal";
import { buildValidationLog } from "helpers/product-collection/validation-log";
import { ExportItemPo } from "constants/analytics";
import { AccountType } from "types/user";
import { IBrandsDataStatePayload } from "reducers/brands-data-reducers";
import { IFetchBrandsDataPayload } from "actions/brands-data-actions";
import { ExportWithWarningsDialog } from "./product-collection-export-to-merchant/export-with-warnings-dialog";
import { Column, ColumnApi, GridApi } from "ag-grid-community";
import { IMutateAllocationBagValue } from "actions/collections";
import { IProductCollectionTileGridContext } from "./product-collection-views/product-collection-grid-view/grid-renderers/full-width-cell-renderer";
import { shallowEqualAndExclude } from "helpers/performance/shallow-compare-exclude";
import { FeatureFlags } from "helpers/feature-flags";
import { DoorGroupMetric, MetricsKind } from "helpers/metrics/types";
import { metricsTimerStart } from "helpers/metrics";
import {
    DimensionsPanelReduxState,
    DimensionsPanelCompositeReduxState,
} from "reducers/dimensions-panel/dimensions-panel";
import { buildExportSnapshotQuery } from "helpers/query-dsl-builder";
import { SetApplied } from "actions/tool-panel/applied/types";
import { getLegacyTypeByMetricsKind } from "helpers/metrics/legacy-metrics";
import { IFilters } from "./product-collection-toolbar/product-collection-toolbar-filters/types";
import { ManageAllocationDimension } from "./manage-allocation-dimension/manage-allocation-dimension";
import { TabValues } from "./manage-allocation-dimension/constants";
import { UploaderProvider } from "./manage-allocation-dimension/import-door-attributes/context-api/providers";
import { DialogDoubleCheckChanges } from "./manage-allocation-dimension/import-door-attributes/dialog-double-check-changes";
import { Location } from "history";
import { AgGridReact } from "ag-grid-react";
import { FinancialTargetGrid } from "./product-collection-views/product-collection-target-metrics/target-metrics-grid";
import { GridSplit } from "./product-collection-views/product-collection-grid-view/grid-split/grid-split";
import { TargetGroupWithTargets } from "./footer/types";
import { DisplayedGridRows } from "reducers/collection-target/types";
import { Option } from "./footer/components/left-side/manage-distribution-modal/components/select";
import { Distribution } from "actions/collections/collection-settings";

export const getGridParamsFromLocation = ({ query }): IGridParamsQueryParam => {
    let { gridParams } = query;
    try {
        gridParams = JSON.parse(gridParams);
    } catch (e) {
        gridParams = {};
    }

    return gridParams;
};

export const getSearchFromLocation = ({ query }): string => {
    const { search } = query;

    return search;
};

export const getAllocationDimensionsByKind = memoizeOne(
    (allocationDimensions: IAllocationDimension[], kind: AllocationDimensionKind) => {
        return allocationDimensions.filter((allocationDimension) => allocationDimension.subtype === kind);
    }
);

export const getGenericAllocationDimensions = (allocationDimensions: IAllocationDimension[]) =>
    getAllocationDimensionsByKind(allocationDimensions, AllocationDimensionKind.Generic);

export const getUniqueBrandEntities = memoizeOne(
    (brands, items: List<any>) => {
        return (
            brands &&
            items
                ?.reduce((acc, item) => acc.add(item.getIn(Item.FIELDS.BRAND_ID)), OrderedSet<string>())
                .map((brandId) => brands?.[brandId])
                .toJS()
        );
    },
    (newArgs, oldArgs) => {
        const [newBrands, newItems] = newArgs;
        const [oldBrands, oldItems] = oldArgs;

        // We don't need new copy if brands are equal and number of items are equal
        return newBrands === oldBrands && newItems?.size === oldItems?.size;
    }
);

const getFilteredTable = (bulks, datesId, doorsId) => {
    let r = [];

    if (datesId) {
        r = bulks.r.filter((item) => datesId.includes(item.c[0]) && doorsId.includes(item.c[1]));
    } else if (doorsId) {
        r = bulks.r.filter((item) => doorsId.includes(item.c[0]));
    }

    return {
        ...bulks,
        r,
    };
};

export const cleanItemsbyDoorsAndDates = (itemsToCopy, dates, doors) => {
    const datesId = dates ? dates.map((element) => element.id) : null;
    const doorsId = doors ? doors.map((element) => element.id) : null;
    const bagsToClean = [
        ItemFields.Allocation,
        ItemFields.Bulks,
        ItemFields.PackAllocation,
        ItemFields.PoCreationExports,
    ];
    return itemsToCopy.map((element) => {
        const elementClone = { ...element };
        bagsToClean.forEach((bag) => {
            if (element[bag] !== undefined && (datesId !== null || doorsId !== null)) {
                const filteredData = getFilteredTable(element[bag], datesId, doorsId);
                if (filteredData.r.length) {
                    elementClone[bag] = filteredData;
                } else {
                    delete elementClone[bag];
                }
            }
        });
        return elementClone;
    });
};

interface IItemForProductModal {
    item: any;
    itemId: string;
    itemIndex: number;
    productId: string;
    brandId: string;
}

export enum TooPanelWidth {
    Collapsed = 22,
    Small = 221,
    Large = 351,
}
interface IProps {
    me: any;
    app: any;
    onAddItem: (itemsId, attributes?, index?) => void;
    itemDisplayProperty?: List<any>;
    doorGroupMetricsSchema: List<DoorGroupMetric>;
    itemsId: string;
    itemsRevision: number;
    bulkItems: any;
    onBulkItemsClearAll: () => void;
    onBulkItemsSet: (ids: OrderedSet<string>) => void;
    onBulkItemsToggleSelectAll: () => void;
    onDeleteItemsViaBulkItems: (itemsId, bulkItems, options?) => void;
    clearBagValues: (itemIds, bagTypes) => void;
    onCreateCollectionAsset?: any;
    collectionId?: string;
    collection?: Map<string, any>;
    capabilities: nuorder.folder.v1.Capabilities;
    collectionDriveItem: nuorder.folder.v1.Item;
    customFilteredItems?: Set<string>;
    items?: List<any>;
    onMutateCollectionBudget: (collectionId: string, value: any) => void;
    onMutateReviewPlan: (dimensionPath: string[], totalType: any, value: string | number) => void;
    onMutateMetricsReviewPlan: (dimensionPath: string[], metricsKind: MetricsKind, value: string | number) => void;
    onMutateCollectionItem: (itemsId, index, item, value?) => void;
    onMutateDefaultItem: (item: Map<string, any>, value?: any) => void;
    onMutateAllocationDimensionValue: (allocationDimensionId: string, item) => void;
    mutateDeliveryPurchaseOrder: (deliveryPurchaseOrder: IDeliveryPurchaseOrder, index: number, meta?: any) => void;
    onRemoveItem: (itemsId: string, index: number) => void;
    onMoveItem: (itemsId: string, index: number, destinationIndex: number, rowId: string) => void;
    onMoveAllocationDimensionValue: (allocationDimensionId: string, index: number, destinationIndex: number) => void;
    onGridMount: (api: any, columnApi: any) => void;
    userPermission?: string;
    hashtags: Set<string>;
    gridApi?: GridApi;
    columnApi?: ColumnApi;
    scrollToIndex?: number;
    onUpdateCollectionAttribute: (id, prop, value, reset?) => void;
    onOpenCollectionSettings: () => void;
    onDeleteCollection: (id) => void;
    onFetchContacts: (search: string, merchantId: string, brandId?: string) => void;
    userEntities: any;
    users: any;
    onChangeContactSearchTerm: any;
    onAddPermission: (params) => void;
    onRemovePermission: () => void;
    onUpdatePermission: (params) => void;
    onSnackbarAdd: (params) => void;
    onSetContact: (user) => void;
    location: any;
    brandId?: string;
    router: any;
    syncingProducts: boolean;
    allocatingAssortment: boolean;
    findingSizes: boolean;
    onSyncProductsStart: (isFiltered?: boolean) => void;
    onSyncProductsStop: () => void;
    onFocusChange: (collectionId: string, itemsId: string, field: string) => void;
    focusOnCellMap: Map<string, any>;
    activeUserSessions: Map<string, any>;
    brandEntities: any;
    brandsDataById: IBrandsDataStatePayload["brandsDataById"];
    isMobileOrPhablet: boolean;
    isMobileOrPhaTablet: boolean;
    onCreateCollectionExport: (params: IExportParams) => void;
    onCreateNordstromPush: (params: any) => void;
    collectionExport: any;
    onClearCollectionExport: () => void;
    panel?: Panel;
    onSubscribeToItemsUpdates: () => void;
    onUnsubscribeToItemsUpdates: () => void;
    poiEntities?: { [collectionPoiId: string]: CollectionPOI };
    scope: any;
    isSaving?: boolean;
    onFetchGlobalProducts: (params) => void;
    onFetchNextGlobalProducts: (params) => void;
    globalProducts: any;
    globalProductState?: any;
    onResetGlobalProducts: (params) => void;
    isFetchingModal: boolean;
    onUndo: () => void;
    onRedo: () => void;
    bulkAddingItems: boolean;
    onFetchProducts: (productId: string, brandId: string) => void;
    isFetchingProducts: boolean;
    variantIds: any;
    productError: any;
    productId: string;
    collectionProduct: any;
    onFetchCollectionPOIs: () => void;
    collectionProducts: any;
    onToggleAddSMUDialog: () => void;
    onChangeCurrentView: (currentView: List<Map<string, any>>) => void;
    currentViewName: string;
    currentAllocationDimensionView: IPreference[];
    budgets: null | Map<any, any>;
    collectionBudgetActuals: any;
    copiedDoorAllocationName?: string;
    onPasteDoorAllocations: (itemsId: string, door: string, filterAssortmentByBrand: string) => void;
    deliveryPurchaseOrders: IDeliveryPurchaseOrder[];
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    onToggleTimeline: () => void;
    showTimeline: boolean;
    onSetSuggestions: (data: ISuggestionItem[], storeKey: string) => void;
    onFetchCollectionPOI: (poi: string) => void;
    onGetSuggestions: () => ISuggestionObject;
    calculateBulkDoorTotals: (rows: List<any>, doors, useAsFilteredTotals?: boolean) => void;
    calculateBulkDoorTotalsFiltered: (rows: List<any>, doors) => void;
    onSubscribe: (targetFeedId: string) => void;
    onUnsubscribe: (targetFeedId: string) => void;
    subscription: ISubscription;
    tileViewData: IGridData;
    tileGridData: IGridData;
    onSetTileViewData: (filteredGridData: IGridData, allGridData: IGridData) => void;
    onAllocate: (
        action: CollectionAction,
        ids: string[],
        rawItemDisplayProperty: ISchemaKey[],
        merchantId: string
    ) => void;
    onMatchSelectedSizesWithSizeRanges: (action: CollectionAction, ids: string[], filterByBrand: string) => void;
    allocationEnabled: boolean;
    sizingAllocationEnabled: boolean;
    sizingRangeEnabled: boolean;
    sizingCurveEnabled: boolean;
    sizePackEnabled: boolean;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    // doors collection related
    dates: IAllocationDimensionValue[];
    doors: IDoor[];
    doorClassificationSchema: ISchemaKey[];
    onClearAllocations: (itemId: string) => void;
    dataEntryType?: UnitsEntryType;
    sizeRanges: ISizeRange[];
    onResetSizeRanges: () => void;
    onLookupAssortmentErrors: (assortmentsData?: Array<{ [key: string]: string }>) => void;
    allocationDimensions: IAllocationDimension[];
    rawAllocationDimensions: IAllocationDimension[];
    syncingAllocationDimension: boolean;
    onCopy: (params: ICopyProps) => void;
    isRollupMode?: boolean;
    rollupType?: RollupType;
    onExportToExcel: () => void;
    syncingReviewPlans: boolean;
    reviewPlanRows: object[];
    reviewPlan: object;
    onFetchPinnedRows: (showTotals: boolean, dimensionPlanMetrics: string[], accessPlan: boolean) => void;
    doorTotals: IDoorTotals;
    doorTotalsFiltered: IDoorTotals;
    doorTotalRowFiltered?: IDoorTotalRow;
    isCollectionUploading: boolean;
    sizeAllocationType?: SizeAllocationType;
    sizeTableType?: string;
    onSetViewToDefault: (collectionId: string) => void;
    merchantCurrency?: ICurrency;
    defaultDateFormat?: string;
    listMerchantCollectionVersionStatutes: ICollectionVersionStatus[];
    onAddCollectionTag: (id: string) => void;
    onRemoveCollectionTag: () => void;
    poiTags: List<any>;
    currentPoi?: string;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    fetchMerchantPreference: (merchantId) => void;
    fetchMerchantAllocationDimensions: (merchantId) => void;
    genericMerchantAllocationDimensions: IAllocationDimension[];
    requestingMerchantAllocationDimensions: boolean;
    defaultItem: Map<keyof IGridItem, IGridItem[keyof IGridItem]>;
    sizePacksTolerance: ISizePacksTolerance | null;
    updateCollectionSizePacksTolerance: (sizePacksTolerance: ISizePacksTolerance | null) => void;
    canShopBrand?: boolean;
    tileSettings: ITileSettings;
    onGetTileSettings: () => ITileSettings;
    onApplyTileSettings: (tileSettings: ITileSettings, tileViewKey: number | string) => void;
    onResetTileSettings: () => void;
    selectedTileViewKey: number | string;
    onClearAssortmentError: () => void;
    isFetchingBrandsData: boolean;
    onFetchBrandsData: (query: IFetchBrandsDataPayload["query"]) => void;
    onSetDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetFilteredDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetDoorTotalRow?: (doorTotals: IDoorTotalRow) => void;
    recreateServerRollupSnapshot?: boolean;
    onFetchServerRollupSnapshotItem?: (
        merchantId: string,
        snapshotId: string,
        itemId: string,
        collectionId: string,
        itemDisplayProperty: List<Map<string, any>>
    ) => void;
    onSetServerRollupSnapshotItem: (itemId: string, collectionId: string, product: ISnapshotItem) => void;
    snapshotItems?: Map<string, ISnapshotItem>;
    isFetchingSnapshotItem?: boolean;
    onSetImageCellDimensions: (dimensions: IImageCellDimensions) => void;
    imageCellDimensions: IImageCellDimensions;
    merchantFeaturePreference?: nuorder.collection.merchant_feature_config.MerchantFeatureConfig;
    onCreateCollectionExportItemsPO: (params) => void;
    errorLog?: Map<string, any>;
    onUpdateValidationLog: (mode: string, newLog: nuorder.collection.validation_log.ValidationLog) => void;
    validationProperties: List<Map<string, any>>;
    onSyncInventoryUnits: (action: CollectionAction, ids: string[]) => void;
    syncingInventoryUnits: boolean;
    inventoryEnabled: boolean;
    userMerchantId: string;
    isServerRollup: boolean;
    flags?: FeatureFlags;
    syncBulkWithAllocation: (rowId: string) => void;
    onMutateBagValues: (newBagValue: IMutateAllocationBagValue) => void;
    replaceBagValues: (bagType: AllocationBagType, rowId: string, bagValues: BagValues) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    onCustomDoorCellValueChanged: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
    updateAllocationDimensionAttribute: (
        allocationDimensionAttribute: ISchemaKey[],
        allocationDimensionId: string
    ) => void;
    dimensionsPanel: DimensionsPanelCompositeReduxState;
    dimensionsPanelApplied: DimensionsPanelReduxState;
    dimensionsPanelDraft: DimensionsPanelReduxState;
    setApplied: SetApplied;
    exportOptions?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    startingMutationCount?: number;
    hasExportLogs?: boolean;
    onFetchMoreRows: () => void;
    showTooManyOperationsDialog: () => void;
    onStartCreateRollupSnapshot: () => void;
    onFetchCollectionDriveItem: (merchantId: string, rollupId: string, collectionIds: string[]) => void;
    exportItemPO?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    fetchBothITemPO: (status: any) => void;
    isFinancialTargetGridOpen: boolean;
    merchantAllocationDimensions?: IAllocationDimension[];
    targetGroupsWithTargets: TargetGroupWithTargets[];
    targetBag: AllocationBag;
    toggleFinancialTargetGrid: () => void;
    selectedTargetIds: Set<string>;
    displayedFinancialTargetGridRows: DisplayedGridRows;
    openDistributionModal: () => void;
    distributionTargets: DistributionTargets;
    selectedDistribution: Option | undefined;
    setSelectedDistribution: (value: Distribution) => void;
    isCollapseModeOn: boolean;
}

interface IState {
    searchText: string;
    defaultSearchText: { value: string }; // should be immutable
    view: View;
    isCloneConfirmationOpen: boolean;
    isDeleteConfirmationOpen: boolean;
    itemIds?: any;
    itemsId?: any;
    gridParams: any;
    rowDataSet: boolean;
    notifyItemModalConflict: boolean;
    itemIdToLink?: string;
    filterAssortmentByBrand?: string;
    filterAssortmentBySMU?: string;
    toolpanelOpened: ToolPanels | null;
    toolpanelIsBig: boolean;
    tileViewData?: IGridData;
    formulaFields: IDisplayFields;
    manageDoorGroupingOpen: boolean;
    manageAllocationDimensionOpen: boolean;
    manageAllocationDimensionTab: TabValues;
    managePurchaseOrdersOpen: boolean;
    isConfigureTileViewOpen: boolean;
    isViewFullDetailOpen: boolean;
    viewFullDetailProductId: string;

    // Interface for images
    isDeleteImageModalOpen: boolean;
    uploading: boolean;
    itemForProductModal?: Partial<IItemForProductModal>;

    // schema fields
    searchableFields: string[][];
    allowedValueFields: any;
    cardViewFields: IDisplayFields;
    shopViewFields: IDisplayFields;
    detailFields: IDisplayFields;
    detailViewFields: List<any>;
    schemaFields: List<any>;
    sortableFields: Array<Map<string, any>>;
    filterableFields: Array<Map<string, any>>;
    groupableFields: Array<Map<string, any>>;
    copiedAllocation: Map<string, any> | null;
    copiedAllocationName: string;
    copiedAllocationSizes: string;
    copiedBulks?: Map<string, any>;
    copyItemsString: string;
    isResetColumns: boolean;
    requiredFieldNames: string[];
    desiredFieldNames: string[];
    detailDisplayFields: List<any>;
    validatePOTable: boolean;
    uniqueBrandEntities: any[];
    exportDialogOpen: boolean;
    pinnedRowColumnOrder?: any;
    showDefaultItem: boolean;
    tileSettings: ITileSettings;
    selectedTileViewKey: boolean;
    activeValidationMode?: ValidationModes;
    itemsToBeValidated?: Set<any>;
    validationOutcome?: [number, number, number, number];
    exportToMerchantDialogOpen: boolean;
    tileViewSize: string;
    desiredFields: string[];
    exportWithWarningsDialogOpen: boolean;
    exportItemsCollection: any;
    exportToMerchantConfirmationDialogOpen: boolean;
    // Expand All Groups
    expandAllGroups: boolean;
    tileGridContext?: IProductCollectionTileGridContext;
    scrollToDoorsColumnOnLoad: boolean;
    nodeRowIndex?: number;
    savedViewData?: any;
    statusExportItemPO?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    mainGridRef: React.RefObject<AgGridReact> | null;
    switchingTile?: boolean;
}
const VIEWS = [View.Grid, View.Tile, View.History];
export const productCollectionStyles = memoizeOne(() => {
    return merge(
        {
            height: "100%",
            // minHeight: "100%",
            // maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            flexShrink: 1,
        },
        $(" .assortmentContainer", {
            display: "flex",
            height: "100%",
            background: "white",
        }),
        $(" .assortmentMain", {
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
        }),
        $(" .assortmentMainTileView .ag-root", {
            display: "none !important",
        }),
        $(" .assortmentMainTileView .ag-side-bar", {
            position: "absolute",
            right: "0px",
            height: "100%",
        }),
        $(" .assortmentMainTileView .ag-root-wrapper", {
            borderLeft: "1px solid #BDC3C7",
        }),
        $(" .tileViewMain", {
            height: "100%",
            display: "flex",
        }),
        $(" .tileViewMain .ag-theme-balham > div", {
            top: 0,
        }),
        $(" .ag-theme-balham", {
            flexGrow: 1,
            flexShrink: 1,
        }),
        $(" .assortmentMainTileView .ag-theme-balham", {
            flexGrow: 0,
            flexShrink: 0,
        }),
        $(" .assortmentMainTileView.toolbarSmall .ag-theme-balham", {
            width: `${TooPanelWidth.Small}px !important`,
        }),
        $(" .assortmentMainTileView.toolbarLarge .ag-theme-balham", {
            width: `${TooPanelWidth.Large}px !important`,
        }),
        $(" .assortmentMainTileView.toolbarCollapsed .ag-theme-balham", {
            width: `${TooPanelWidth.Collapsed}px !important`,
        }),
        $(" .ag-tool-panel-horizontal-resize", {
            display: "none !important",
        }),
        $(" .toolbarSmall .ag-side-bar", {
            width: `${TooPanelWidth.Small - 1}px !important`,
        }),
        $(" .toolbarSmall .ag-tool-panel-wrapper", {
            width: `${TooPanelWidth.Small - 1}px !important`,
        }),
        $(" .toolbarLarge .ag-side-bar", {
            width: `${TooPanelWidth.Large - 1}px !important`,
        }),
        $(" .toolbarLarge .ag-tool-panel-wrapper", {
            width: `${TooPanelWidth.Large - 1}px !important`,
        }),
        media(
            "(max-width: 500px)",
            $(" .toolbarSmall .ag-theme-balham", {
                width: `${TooPanelWidth.Collapsed}px !important`,
            }),
            $(" .toolbarSmall .ag-theme-balham > div", {
                left: "auto",
                width: `${TooPanelWidth.Small}px`,
            }),
            $(" .toolbarLarge .ag-theme-balham", {
                width: `${TooPanelWidth.Collapsed}px !important`,
            }),
            $(" .toolbarLarge .ag-theme-balham > div", {
                left: "auto",
                width: `${TooPanelWidth.Large}px`,
            })
        ),
        $(" div.toastContent button", {
            background: "none",
            border: "none",
            textDecoration: "underline",
        }),
        $(" div.toastContent div.validationSummary", {
            minWidth: "350px",
        }),
        $(" div.toastContent div.validationSummary h4", {
            marginTop: 0,
            marginBottom: 0,
            color: "white",
        })
    );
});
const SUCCESSIVE_CLICKS_TIME_MS = 300;
export class ProductCollection extends React.Component<IProps, IState> {
    // We can switch here from PureComponent to Component as we are implementing shouldComponentUpdate
    static contextTypes = {
        me: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired,
    };

    state: IState;
    private _isMounted = false;
    declare targetMetricsGridRef;
    declare gridViewRef: React.RefObject<ProductCollectionGridView>;

    constructor(props, context) {
        super(props, context);
        const { search, cartView, expand } = props.location.query;

        this.state = {
            expandAllGroups: expand === "false" ? false : true,
            isCloneConfirmationOpen: false,
            isDeleteConfirmationOpen: false,
            searchText: search || "",
            defaultSearchText: { value: search || "" },
            itemIds: undefined,
            view: VIEWS.includes(cartView) ? cartView : context.me.last_assortment_view,
            isDeleteImageModalOpen: false,
            uploading: false,
            rowDataSet: false,
            notifyItemModalConflict: false,
            toolpanelOpened: ToolPanels.Columns,
            toolpanelIsBig: false,
            filterAssortmentBySMU: SMU_FILTER.All,
            ...parseSchema(props.itemDisplayProperty),
            ...this.parseProductModalParams(),
            gridParams: getGridParamsFromLocation(props.location),
            copiedAllocation: null,
            copiedAllocationName: "",
            copiedAllocationSizes: "",
            copyItemsString: "",
            isResetColumns: false,
            doors: props.doors,
            manageDoorGroupingOpen: false,
            manageAllocationDimensionOpen: false,
            managePurchaseOrdersOpen: false,
            isViewFullDetailOpen: false,
            viewFullDetailProductId: "",
            validatePOTable: false,
            uniqueBrandEntities: getUniqueBrandEntities(props.brandEntities, props.items),
            exportDialogOpen: false,
            showDefaultItem: false,
            isConfigureTileViewOpen: false,
            exportToMerchantDialogOpen: false,
            exportWithWarningsDialogOpen: false,
            desiredFields: undefined,
            exportItemsCollection: undefined,
            scrollToDoorsColumnOnLoad: false,
            savedViewData: undefined,
            mainGridRef: null,
            switchingTile: true,
        };

        this.state.tileGridContext = this.computeTileGridContext(props, this.state);

        // initial fetch of POIs
        const { editable } = this.permissions(cartView, this.props.capabilities);
        if (editable) {
            this.props.onFetchCollectionPOIs();
        }

        this.handleOpenProductModalOnItem = debounce(this.handleOpenProductModalOnItem, 200);
        this.targetMetricsGridRef = React.createRef();
        this.gridViewRef = React.createRef();
    }

    componentDidMount() {
        this._isMounted = true;
        window.addEventListener("storage", this.handleCopyItemsFromOtherTab);
        this.handleGetPasteData();
        this.maybeOpenCDP();
    }

    componentWillUnmount() {
        // enable zendesk widget
        // tslint:disable-next-line
        // (window as any).zE && (window as any).zE("webWidget", "show");

        this.handleDeleteItems.cancel();
    }

    UNSAFE_componentWillReceiveProps(nextProps: IProps) {
        if (this.props.itemDisplayProperty !== nextProps.itemDisplayProperty) {
            this.setState(parseSchema(nextProps.itemDisplayProperty));
        }
        if (
            this.props.location.query.cartView !== nextProps.location.query.cartView &&
            VIEWS.includes(nextProps.location.query.cartView)
        ) {
            let newState: any = {
                view: nextProps.location.query.cartView,
            };
            const gridParams = getGridParamsFromLocation(nextProps.location);

            newState = {
                ...newState,
                gridParams,
            };
            if (newState.view === View.Tile && nextProps.bulkItems.size) {
                nextProps.onBulkItemsClearAll();
            }
            this.setState(newState);
        }
        if (this.props.location.query.cartView !== View.History && nextProps.location.query.cartView === View.History) {
            nextProps.onBulkItemsClearAll();
        }
        if (this.props.location.query.expand !== nextProps.location.query.expand) {
            this.setState({
                expandAllGroups: nextProps.location.query.expand === "false" ? false : true,
            });
        }

        if (
            nextProps.location.query &&
            this.state.filterAssortmentByBrand &&
            nextProps.location.query.brand !== this.state.filterAssortmentByBrand
        ) {
            this.setState({
                filterAssortmentByBrand: nextProps.location.query.brand,
            });
        }

        if (this.props.items !== nextProps.items) {
            this.setState({ uniqueBrandEntities: getUniqueBrandEntities(this.props.brandEntities, this.props.items) });
        }

        if (
            this.props.isServerRollup &&
            nextProps.location.query.itemId &&
            nextProps.collection &&
            !this.props.isFetchingSnapshotItem &&
            !nextProps.isFetchingSnapshotItem &&
            !nextProps.snapshotItems.get(
                buildServerRollupRowNodeId(nextProps.location.query.itemId, nextProps.location.query.collectionId)
            )
        ) {
            const snapshotId = nextProps.collectionId;
            const { itemId, collectionId } = nextProps.location.query;
            const merchantId = nextProps.collection?.getIn(["merchant_v1_id", "string_value"], "");
            nextProps?.onFetchServerRollupSnapshotItem?.(
                merchantId,
                snapshotId,
                itemId,
                collectionId,
                nextProps.itemDisplayProperty
            );
        }
    }

    shouldComponentUpdate(nextProps: IProps, nextState: IState) {
        if (this.state.manageAllocationDimensionOpen && nextState.manageAllocationDimensionOpen) {
            return !isEqual(nextProps.allocationDimensions, this.props.allocationDimensions);
        }

        const arePropsNotEqual = shallowEqualAndExclude(this.props, nextProps, ["location"]); // We exclude location as the query params
        // we're listening for are either injected, or managed inside the state. We perform a query comparison on the block below.
        const areStatesNotEqual = shallowEqualAndExclude(this.state, nextState, ["gridParams"]); // We check these
        // properties separately as we don't want to rerender when they are equivalent.

        if (!arePropsNotEqual) {
            return (
                areStatesNotEqual ||
                !isEqual(this.state.gridParams, nextState.gridParams) ||
                !isEqual(
                    { ...this.props.location.query, [DOOR_GROUP_QUERY_KEY]: "", gridParams: "", viewId: "" },
                    { ...nextProps.location.query, [DOOR_GROUP_QUERY_KEY]: "", gridParams: "", viewId: "" }
                )
            );
        }
        return true;
    }

    componentDidUpdate(prevProp: IProps) {
        this.handleTotalsUpdate(prevProp.tileGridData, prevProp.location);

        if (
            this.props.location.query.itemId !== prevProp.location.query.itemId ||
            this.props.location.query.productId !== prevProp.location.query.productId ||
            this.props.items !== prevProp.items
        ) {
            this.maybeOpenCDP();
        }
        this.setTileGridContext(this.computeTileGridContext(this.props, this.state));
        this.handleShouldTargetsGridRemainOpen();
        this.handleUnmountTargetGrid();
    }

    maybeOpenCDP = () => {
        const { itemForProductModal } = this.parseProductModalParams(
            this.props.location.query.productId,
            this.props.location.query.itemId
        );
        this.setState({ itemForProductModal });
    };

    handleTotalsUpdate(prevTileGridData: typeof this.props.tileViewData, prevLocation: Location) {
        const { tileGridData, location } = this.props;
        const isHistoryView = location?.query?.cartView === View.History;
        const isDifferentView = location?.query?.cartView !== prevLocation?.query?.cartView;
        const hasTileDataSizeChanged = prevTileGridData.data.size === 0 && this.props.tileGridData.data.size > 0;
        // Since the reference for tileGridData changes when in history view
        // on every rerender, we have to run a deep equality check to make sure
        // it's equal
        const hasTileViewDataChangedWhenHistoryView = isHistoryView && !isEqual(prevTileGridData, tileGridData);
        const isCartModified = !isEqual(prevTileGridData, tileGridData);
        if (hasTileViewDataChangedWhenHistoryView || isDifferentView || hasTileDataSizeChanged || isCartModified) {
            this.calculateBulkTotals();
        }
    }

    handleCopyAllocation = (copiedAllocation, copiedAllocationName, copiedAllocationSizes, copiedBulks?) => {
        this.setState({ copiedAllocation, copiedAllocationName, copiedAllocationSizes, copiedBulks });
    };

    handlePasteAllocation = (rowData, rowIndex) => {
        const { onMutateCollectionItem, itemsId, allocationDimensions, sizeTableType, allocationBagRef } = this.props;
        const { copiedAllocation, copiedAllocationSizes, copiedBulks } = this.state;
        const stringifiedSizes = rowData.getIn(Item.FIELDS.SIZES);
        // This only checks for stringified arrays, we need to make this deep equality in the future, but it works for now.
        if (stringifiedSizes !== copiedAllocationSizes) {
            this.props.onSnackbarAdd({ message: "Sizes are not the same!" });
            return;
        }

        const isLegacyBag = !allocationBagRef;
        const rowId = amphibiousGetIn(rowData, Item.FIELDS.ID);

        let mutatedItem = rowData;
        if (isLegacyBag) {
            mutatedItem = rowData.setIn(Item.ALLOCATIONS, copiedAllocation);

            if (sizeTableType === SizeTableTypes.Grouped && copiedBulks) {
                mutatedItem = mutatedItem.setIn(Item.BULKS, copiedBulks);
            }
        } else {
            // Mutate local copy of allocation bag
            this.props.allocationBagRef.allocationBag = mutateBagValues(
                AllocationBagType.allocations,
                this.props.allocationBagRef.allocationBag,
                rowId,
                copiedAllocation
            );
            this.props.replaceBagValues(AllocationBagType.allocations, rowId, copiedAllocation);

            if (sizeTableType === SizeTableTypes.Grouped && copiedBulks) {
                this.props.allocationBagRef.allocationBag = mutateBagValues(
                    AllocationBagType.bulks,
                    this.props.allocationBagRef.allocationBag,
                    rowId,
                    copiedBulks
                );
                this.props.replaceBagValues(AllocationBagType.bulks, rowId, copiedBulks);
            }
        }
        const stringSelectedsizes = mutatedItem.getIn(Item.FIELDS.SIZES_SELECTED, "");
        const { curveSizes, sizesAddedManually } = parseSelectedSizes(stringSelectedsizes);
        const { sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
            mutatedItem,
            curveSizes,
            sizesAddedManually,
            allocationDimensions,
            allocationBagRef?.allocationBag
        );

        mutatedItem = mutatedItem.setIn(Item.FIELDS.SIZE_CURVE_STATUS, sizeCurveStatus);

        onMutateCollectionItem(itemsId, rowIndex, mutatedItem);
        this.props.onSnackbarAdd({ message: "Allocation has been pasted." });
    };

    handleCopyItemsString = (newValue: string) => {
        this.setState({
            copyItemsString: newValue,
        });
    };
    handleFocusOnCellMap = () => {
        return this.props.focusOnCellMap;
    };

    handleOnToggleTotals = (forceToggle?: boolean) => {
        const updated = processActionsToDimensionParams(
            {
                type: DimensionParamsActionType.ShowDoorTotals,
                value:
                    forceToggle !== undefined
                        ? forceToggle
                        : !this.props.dimensionsPanelApplied.metrics.showAssortmentTotals,
            },
            this.props.dimensionsPanelApplied
        );
        this.handleDimensionsStateChange(updated);
    };

    // Here we implement a reference logic for dimensionsPanel.
    // The reason is we, when expanding/collapsing door groups, will trigger handleDimensionsStateChange repeatedly.
    // But that, in turn, will update URL query string and recalculate totals, which will block the UI for long time
    // in a wasteful manner, as the only result that matters is the last one, which is compounded.
    private updatedDimensionsStatePool: DimensionsPanelReduxState;

    private _handleDimensionsStateChange = debounce(() => {
        this.handleDimensionsStateChange(this.updatedDimensionsStatePool);
        this.updatedDimensionsStatePool = undefined;
    }, 50);

    handleColumnGroupOpened = (groupId: string, isExpanded: boolean) => {
        if (!this.updatedDimensionsStatePool) {
            this.updatedDimensionsStatePool = this.props.dimensionsPanelApplied;
        }

        this.updatedDimensionsStatePool = processActionsToDimensionParams(
            {
                type: isExpanded ? DimensionParamsActionType.ExpandGroup : DimensionParamsActionType.CollapseGroup,
                value: groupId,
                inUrl: isExpanded && groupId === ColumnTypes.AllDimensionTotal,
            },
            this.updatedDimensionsStatePool
        );
        this._handleDimensionsStateChange();
    };

    shouldTurnOnDoors = (forceValue?: boolean) => {
        if (!forceValue && !this.isDoorsGroupShown()) {
            const containsDimensions = this.state.gridParams?.[GridParamType.RowGroup]?.some((column) =>
                column.includes(ALLOCATION_DIMENSION_ID_PREFIX)
            );
            if (containsDimensions) {
                return false;
            }
        }
        return true;
    };

    handleOnToggleDoors = (forceValue?: boolean) => {
        if (this.shouldTurnOnDoors() === false) {
            return;
        }

        const updated = processActionsToDimensionParams(
            {
                type: DimensionParamsActionType.ShowDoorsGroup,
                value: forceValue !== undefined ? forceValue : !this.isDoorsGroupShown(),
            },
            this.props.dimensionsPanelApplied
        );
        this.handleDimensionsStateChange(updated);
    };

    handleOnChangeDoorGrouping = (_: React.MouseEvent, doorGrouping: string) => {
        const updated = processActionsToDimensionParams(
            {
                type: DimensionParamsActionType.ShowClassification,
                // If same classification is clicked, toggle it off
                value: this.getActiveClassification() === doorGrouping ? undefined : doorGrouping,
            },
            this.props.dimensionsPanelApplied
        );
        this.handleDimensionsStateChange(updated);
    };

    handleApplyDoorGroupings = () => {
        if (this.props.isServerRollup && this.props.doorGroupMetricsSchema?.size > 0) {
            // Remove only delivery rowGroups - maybe should apply to assortments as well
            const dimensionGroup = this.state.gridParams[GridParamType.RowGroup]?.some((it) =>
                it.startsWith(ALLOCATION_DIMENSION_ID_PREFIX)
            );
            const sortedByGroup = this.state.gridParams[GridParamType.Sort]?.some(
                (it) => it.colId === "ag-Grid-AutoColumn"
            );
            if (dimensionGroup) {
                this.handleGridParamsChange([
                    {
                        type: SaveStateToGridQueryParamsType.SetRowGroup,
                        value: this.state.gridParams[GridParamType.RowGroup].filter(
                            (it) => !it.startsWith(ALLOCATION_DIMENSION_ID_PREFIX)
                        ),
                    },
                    ...(sortedByGroup
                        ? [
                              {
                                  type: SaveStateToGridQueryParamsType.SetSort,
                                  value: undefined,
                              },
                          ]
                        : []),
                ]);
            }
        }

        metricsTimerStart();
    };

    handleOnChangeDoorGroupingLabel = (_event: React.MouseEvent, doorGrouping: string) => {
        const updated = processActionsToDimensionParams(
            {
                type: DimensionParamsActionType.ShowClassificationLabel,
                // If same classification is clicked, toggle it off
                value: this.getActiveClassificationLabel() === doorGrouping ? undefined : doorGrouping,
            },
            this.props.dimensionsPanelApplied
        );
        this.handleDimensionsStateChange(updated);
    };

    setScrollToDoorsColumnOnLoad = (newValue: boolean) => {
        this.setState({
            scrollToDoorsColumnOnLoad: newValue,
        });
    };

    fetechSavedViewData = (viewData: string[]) => {
        this.setState({
            savedViewData: viewData,
        });
    };

    handleChangePlanMetric = (_event: React.MouseEvent, planMetric: string) => {
        const selectedPlanMetrics = [...this.getActivePlanMetrics()];
        const indexForNewMetric = selectedPlanMetrics.indexOf(planMetric);

        if (indexForNewMetric !== -1) {
            selectedPlanMetrics.splice(indexForNewMetric, 1);
        } else {
            selectedPlanMetrics.push(planMetric);
        }
        const orderedPlanMetrics = [];
        Object.keys(TotalTypes).forEach((totalType) => {
            if (selectedPlanMetrics.includes(TotalTypes[totalType])) {
                orderedPlanMetrics.push(TotalTypes[totalType]);
            }
        });

        const updated = processActionsToDimensionParams(
            {
                type: DimensionParamsActionType.SelectPlanMetric,
                value: orderedPlanMetrics.length > 0 ? orderedPlanMetrics : undefined,
            },
            this.props.dimensionsPanelApplied
        );
        this.handleDimensionsStateChange(updated);
    };

    handleDimensionsStateChange = (dimensionsPanelState: DimensionsPanelReduxState) => {
        this.props.setApplied(dimensionsPanelState);
    };

    isDoorsGroupShown = () => {
        return this.props.dimensionsPanelApplied.showDoors;
    };

    getActiveClassification = (): string => {
        return this.props.dimensionsPanelApplied.attributes.find((attribute) => attribute.selected)?.id;
    };

    getActiveClassificationLabel = (): string => {
        return this.props.dimensionsPanelApplied.attributes.find((attribute) => attribute.showLabelInHeader)?.id;
    };

    getActivePlanMetrics = (): string[] => {
        return this.props.dimensionsPanel.applied.metrics.showAssortmentPlan
            ? this.props.dimensionsPanelApplied.metrics.values.map((metric) => getLegacyTypeByMetricsKind(metric.kind))
            : [];
    };

    getGroupingClassificationOrder = () => {
        return this.props.dimensionsPanelApplied.attributes.find((attribute) => attribute.selected)?.sort;
    };

    getGroupingClassificationHide = (): string[] => {
        const activeAttribute = this.props.dimensionsPanelApplied.attributes.find((attribute) => attribute.selected);
        return (
            activeAttribute?.values
                .filter((attributeValue) => attributeValue.selected)
                .map((attributeValue) => attributeValue.id) ?? []
        );
    };

    handleGetPasteData = () => {
        let data = "";
        const navigtorClipboard = (navigator as any).clipboard;
        if (navigtorClipboard && navigtorClipboard.readText && this._isMounted) {
            navigtorClipboard
                .readText()
                .then(
                    (clipboardText) => {
                        data = clipboardText;
                    },
                    () => {
                        data = localStorage.getItem(ASSORTMENT_COPY_ITEMS);
                    }
                )
                .finally(() => {
                    this.setState({ copyItemsString: data });
                });
        } else {
            data = localStorage.getItem(ASSORTMENT_COPY_ITEMS);
            this.setState({ copyItemsString: data });
        }
    };

    handleToggleManageDoorGrouping = () => {
        const { manageDoorGroupingOpen } = this.state;
        this.setState({
            manageDoorGroupingOpen: !manageDoorGroupingOpen,
        });
    };

    handleToggleManageAllocationDimension = () => {
        const { manageAllocationDimensionOpen } = this.state;
        this.setState({
            manageAllocationDimensionOpen: !manageAllocationDimensionOpen,
            manageAllocationDimensionTab: TabValues.AddOrEditAttributes,
        });
    };

    handleToggleImportDoorAttributesDialog = () => {
        const { manageAllocationDimensionOpen } = this.state;
        this.setState({
            manageAllocationDimensionOpen: !manageAllocationDimensionOpen,
            manageAllocationDimensionTab: TabValues.ImportAttributes,
        });
    };

    handleToggleManagePurchaseOrders = () => {
        const { managePurchaseOrdersOpen } = this.state;
        this.setState({
            managePurchaseOrdersOpen: !managePurchaseOrdersOpen,
        });
    };

    handleToggleConfigureTileView = () => {
        const { isConfigureTileViewOpen } = this.state;

        this.setState({
            isConfigureTileViewOpen: !isConfigureTileViewOpen,
        });
    };

    handleToggleConfigureTileViewSize = (imageSize: string) => {
        this.setState({
            tileViewSize: imageSize,
        });
    };

    handleApplyNewTileSettings = (settings) => {
        this.props.onApplyTileSettings(settings, CUSTOM_VIEW_SETTINGS);
        this.handleToggleConfigureTileView();
    };

    handleApplyTileSettingsTemplate = (settings, tileViewKey) => {
        this.props.onApplyTileSettings(settings, tileViewKey);
    };

    handleCloseIsViewFullDetail = () => {
        this.setState({
            isViewFullDetailOpen: false,
        });
    };

    handleOpenIsViewFullDetail = () => {
        this.setState({
            isViewFullDetailOpen: true,
        });
    };

    handleShowDefaultItemRow = (e?: MouseEvent) => {
        e?.preventDefault();
        this.props.defaultItem && this.setState({ showDefaultItem: !this.state.showDefaultItem });
    };

    setViewFullDetailProductId = (id: string) => {
        this.setState({
            viewFullDetailProductId: id,
        });
    };

    onSelectColorVariant = (product) => {
        //NOTE: Read only.
    };

    onOpenExportToMerchant = (validateState: boolean) => {
        this.setState({ validatePOTable: validateState });
    };

    handleMutateAllocationDimensionValue = (allocationDimensionId: string, item) => {
        const { onMutateAllocationDimensionValue } = this.props;

        // pass along mutation
        onMutateAllocationDimensionValue(allocationDimensionId, item);
    };

    handleUpdateDeliveryPurchaseOrder = (deliveryPurchaseOrder, index) => {
        const { mutateDeliveryPurchaseOrder } = this.props;

        mutateDeliveryPurchaseOrder(deliveryPurchaseOrder, index);
    };

    // This is short-term hack to get delivery allocation dimension. Ideally, allocation dimension should have specific kind for that.
    getDeliveryAllocationDimension = () => {
        const genericAllocationDimensions = getGenericAllocationDimensions(this.props.allocationDimensions);
        // We use first generic allocation dimension as Delivery if there is more than one generic allocation dimensions
        const deliveryAllocationDimension =
            genericAllocationDimensions.length > 1 ? genericAllocationDimensions[0] : undefined;

        return deliveryAllocationDimension;
    };

    getGroupedAllocationDimensions = memoizeOne((allocationDimensions, rowGroups = []) => {
        const genericAllocationDimensions = getGenericAllocationDimensions(allocationDimensions);

        // To explode on-demand, pass in only allocation dimensions being used for grouping
        return genericAllocationDimensions.filter((allocationDimension) => rowGroups.includes(allocationDimension.id));
    });

    buildUser = memoizeOne((id, role) => {
        return {
            identity: id,
            role: role,
        };
    });

    onPinnedRowOrder = (columnOrder) => {
        this.setState({
            pinnedRowColumnOrder: columnOrder,
        });
    };

    validateCollectionByMode = (itemsToValidate: IGridItem[], mode: ValidationModes) => {
        const { collectionDriveItem } = this.props;
        const collectionName = nameExtRemover(collectionDriveItem?.name, ItemKind.CollectionDocument);

        const statusSchema =
            mode === ValidationModes.ValidateItems
                ? this.props.itemDisplayProperty.find((schema) => schema.get("kind") === Kinds.EXPORT_ITEM_VALIDATION)
                : this.props.itemDisplayProperty.find((schema) => schema.get("kind") === Kinds.EXPORT_PO_VALIDATION);

        if (statusSchema) {
            const collectionValidationData = {
                id: this.props.collectionId,
                name: collectionName,
            };

            const collectionValidationResult = validateCollectionSchemaRules(
                collectionValidationData,
                this.props.validationProperties
            );
            const itemsValidationResult = validateItemsSchemaRules(
                itemsToValidate,
                this.props.validationProperties,
                this.props.itemDisplayProperty,
                mode,
                {
                    sizePacks: this.props.sizePacks,
                }
            );
            const validationResult = collectionValidationResult.concat(itemsValidationResult);
            this.props.onUpdateValidationLog?.(mode, buildValidationLog(validationResult, this.props.me._id));

            return validationResult;
        }

        return [];
    };

    private getItemsAndValidate = (mode: ValidationModes, highlightCells = true) => {
        const items = [];
        const desiredFields = [];

        const validatedItems = [];
        const erroredItems = [];
        const warnedItems = [];
        const collectionError = [];

        const formulaItems = this.props.itemDisplayProperty
            .filter((schema) => schema.get("kind") === Kinds.FORMULA)
            .map((element) => ({ colId: element.get("field_name"), type: element.get("value_type") })) as List<{
            colId: string;
            type: string;
        }>;

        this.props.gridApi?.getSelectedNodes().forEach((rowNode) => {
            const fields = getFieldsByItems(this.props.gridApi, rowNode, formulaItems);
            const node = rowNode.data;
            items.push({ fields: { ...node.fields, ...fields } });
        });

        if (!items.length) {
            return { validatedItems, erroredItems, warnedItems };
        }

        let newState = {};
        let validationResult;
        if (mode) {
            validationResult = this.validateCollectionByMode(items, mode);
            newState = highlightCells
                ? { activeValidationMode: mode, itemsToBeValidated: Set<any>(items.map((item) => item)) }
                : { activeValidationMode: mode };
        } else {
            const validationResults = {};
            Object.values(ValidationModes).forEach((validationMode) => {
                const result = this.validateCollectionByMode(items, validationMode);
                result.forEach((validation) => {
                    if (validationResults[validation.id]) {
                        const storedValidation = validationResults[validation.id];
                        storedValidation.errors = storedValidation.errors.concat(validation.errors);
                        storedValidation.warnings = storedValidation.warnings.concat(validation.warnings);
                    } else {
                        validationResults[validation.id] = validation;
                    }
                });
            });
            newState = { activeValidationMode: undefined, itemsToBeValidated: undefined };
            validationResult = Object.values(validationResults);
        }

        validationResult.forEach((result) => {
            if (result.id !== "collection") {
                if (result.warnings.length) {
                    result.warnings.forEach((element) => {
                        desiredFields.push(element.field);
                    });
                }
                if (result.errors.length) {
                    erroredItems.push(result.id);
                } else if (result.warnings.length) {
                    warnedItems.push(result.id);
                } else {
                    validatedItems.push(result.id);
                }
            } else if (result.errors.length) {
                collectionError.push(result.id);
            }
        });

        this.setState({
            ...newState,
            ...(validationResult.length
                ? {
                      validationOutcome: [
                          validatedItems.length,
                          warnedItems.length,
                          erroredItems.length,
                          collectionError.length,
                      ],
                  }
                : {}),
        });
        if (desiredFields.length > 0) {
            this.setState({
                desiredFields: _.uniq(desiredFields),
            });
        }
        const exportMode =
            mode === ValidationModes.ValidatePOs ? ExportItemPo.ValidatePoData : ExportItemPo.ValidateItemData;
        WindowAnalytics.track(exportMode, {
            items: items.length,
            successRate: parseFloat(((validatedItems.length / items.length) * 100).toFixed(2)),
        });
        return { validatedItems, erroredItems, warnedItems };
    };

    handleValidationOutcomeClose = () => this.setState({ validationOutcome: undefined });

    setTileGridContext = (context: IProductCollectionTileGridContext) => {
        const { tileGridContext } = this.state;
        if (!tileGridContext || !isEqual(context, tileGridContext)) {
            this.setState({ tileGridContext: context });
        }
    };

    getCurrentTileViewSize = () => {
        return this.state.tileViewSize;
    };

    computeTileGridContext = (props: IProps, state: IState): IProductCollectionTileGridContext => {
        const {
            collectionId,
            brandEntities,
            rawAllocationDimensions,
            merchantCurrency,
            tileSettings,
            onGetTileSettings,
        } = props;
        const { cardViewFields, formulaFields, gridParams, tileViewSize = TileViewSize.Medium } = state;
        const rawItemDisplayProperty = getRawItemDisplayProperty(this.props.itemDisplayProperty);
        const newState = {
            collectionId,
            brandEntities,
            cardViewFields,
            formulaFields,
            allocationDimensions: rawAllocationDimensions,
            gridParams,
            dimensionsPanelApplied: props.dimensionsPanelApplied,
            merchantCurrency,
            tileSettings,
            onGetTileSettings,
            rawItemDisplayProperty,
            tileViewSize,
            getTileViewSize: this.getCurrentTileViewSize,
            onItemInvoked: this.handleOpenProductModalOnItem,
        };

        return newState;
    };

    handleNoPurchaseOrders = () => {
        this.props.onSnackbarAdd({ message: formatMessage("Unable to get list of Purchase Orders. Please reload.") });
    };

    handleShouldTargetsGridRemainOpen = () => {
        const { location, isFinancialTargetGridOpen, toggleFinancialTargetGrid } = this.props;
        const isHistoryView = location.query.cartView === View.History;
        const gridParams = location.query?.gridParams ? JSON.parse(location.query.gridParams) : {};
        const isPivotMode = gridParams.pivot?.mode;

        const shoudDisableGrid = isPivotMode || isHistoryView;

        if (shoudDisableGrid && isFinancialTargetGridOpen) {
            toggleFinancialTargetGrid();
        }
    };

    handleUnmountTargetGrid = () => {
        const { isFinancialTargetGridOpen } = this.props;

        if (!isFinancialTargetGridOpen && this.targetMetricsGridRef.current) {
            this.targetMetricsGridRef.current = null;
        }
    };

    getAlignedGrids = () => {
        return this.targetMetricsGridRef.current ? [this.targetMetricsGridRef.current] : undefined;
    };

    render() {
        const {
            brandEntities,
            bulkItems,
            collection,
            capabilities,
            collectionDriveItem,
            collectionExport,
            collectionId,
            customFilteredItems,
            focusOnCellMap,
            globalProducts,
            globalProductState,
            gridApi,
            columnApi,
            hashtags,
            isFetchingModal,
            isMobileOrPhablet,
            isMobileOrPhaTablet,
            itemDisplayProperty,
            items,
            itemsId,
            itemsRevision,
            location,
            brandId,
            onAddItem,
            onBulkItemsSet,
            onBulkItemsClearAll,
            onClearCollectionExport,
            onCreateCollectionAsset,
            onDeleteCollection,
            onFetchGlobalProducts,
            onFetchNextGlobalProducts,
            onFocusChange,
            onMoveItem,
            onMutateCollectionBudget,
            onMutateReviewPlan,
            onMutateMetricsReviewPlan,
            onMutateCollectionItem,
            onRedo,
            onRemovePermission,
            onResetGlobalProducts,
            onSnackbarAdd,
            onSyncProductsStart,
            onSyncProductsStop,
            onUndo,
            onUpdateCollectionAttribute,
            onOpenCollectionSettings,
            router,
            scrollToIndex,
            syncingProducts,
            allocatingAssortment,
            findingSizes,
            bulkAddingItems,
            onToggleAddSMUDialog,
            onChangeCurrentView,
            me,
            budgets,
            collectionBudgetActuals,
            copiedDoorAllocationName,
            onPasteDoorAllocations,
            currentViewName,
            currentAllocationDimensionView,
            showTimeline,
            onToggleTimeline,
            subscription,
            onSubscribe,
            onUnsubscribe,
            tileViewData,
            onSetTileViewData,
            allocationEnabled,
            sizingAllocationEnabled,
            sizingRangeEnabled,
            sizingCurveEnabled,
            sizePackEnabled,
            dates,
            doors,
            doorClassificationSchema,
            onClearAllocations,
            sizeRanges,
            onResetSizeRanges,
            onLookupAssortmentErrors,
            allocationDimensions,
            onMoveAllocationDimensionValue,
            syncingAllocationDimension,
            syncingReviewPlans,
            onCopy,
            isRollupMode,
            rollupType,
            onExportToExcel,
            reviewPlanRows,
            reviewPlan,
            onFetchPinnedRows,
            onMatchSelectedSizesWithSizeRanges,
            isCollectionUploading,
            doorTotals,
            doorTotalsFiltered,
            doorTotalRowFiltered,
            deliveryPurchaseOrders,
            merchantPurchaseOrderDoorGroups,
            sizeAllocationType,
            sizePacks,
            sizeTableType,
            merchantCurrency,
            defaultDateFormat,
            listMerchantCollectionVersionStatutes,
            onAddCollectionTag,
            onRemoveCollectionTag,
            poiTags,
            currentPoi,
            poiToItemsToSizeBulksMap,
            onFetchCollectionPOI,
            genericMerchantAllocationDimensions,
            fetchMerchantPreference,
            fetchMerchantAllocationDimensions,
            requestingMerchantAllocationDimensions,
            onMutateDefaultItem,
            canShopBrand,
            onResetTileSettings,
            selectedTileViewKey,
            tileSettings,
            onSetDoorTotals,
            onSetFilteredDoorTotals,
            onSetDoorTotalRow,
            recreateServerRollupSnapshot,
            snapshotItems,
            onSetImageCellDimensions,
            imageCellDimensions,
            merchantFeaturePreference,
            onCreateCollectionExportItemsPO,
            errorLog,
            syncingInventoryUnits,
            inventoryEnabled,
            userMerchantId,
            onSetServerRollupSnapshotItem,
            dataEntryType,
            onFetchBrandsData,
            isFetchingBrandsData,
            brandsDataById,
            isServerRollup,
            allocationBagRef,
            onCustomDoorCellValueChanged,
            updateAllocationDimensionAttribute,
            startingMutationCount,
            showTooManyOperationsDialog,
            onStartCreateRollupSnapshot,
            onFetchCollectionDriveItem,
            merchantAllocationDimensions,
        } = this.props;

        const {
            allowedValueFields,
            cardViewFields,
            shopViewFields,
            copiedAllocation,
            copiedAllocationName,
            copiedAllocationSizes,
            defaultSearchText,
            detailFields,
            detailViewFields,
            formulaFields,
            filterAssortmentBySMU,
            gridParams,
            itemForProductModal,
            itemIdToLink,
            notifyItemModalConflict,
            requiredFieldNames,
            desiredFieldNames,
            copyItemsString,
            view,
            rowDataSet,
            schemaFields,
            searchableFields,
            searchText,
            toolpanelOpened,
            toolpanelIsBig,
            manageDoorGroupingOpen,
            manageAllocationDimensionOpen,
            managePurchaseOrdersOpen,
            isViewFullDetailOpen,
            viewFullDetailProductId,
            detailDisplayFields,
            validatePOTable,
            uniqueBrandEntities,
            pinnedRowColumnOrder,
            showDefaultItem,
            isConfigureTileViewOpen,
            itemsToBeValidated,
            activeValidationMode,
            validationOutcome,
            tileViewSize = TileViewSize.Medium,
            expandAllGroups,
            tileGridContext,
            scrollToDoorsColumnOnLoad,
            manageAllocationDimensionTab,
        } = this.state;

        const { editable, userRole, accessPlan } = this.permissions(location.query.cartView, capabilities);
        const user = this.buildUser(me._id, userRole);
        let filterByBrand: string;
        if (brandId) {
            filterByBrand = brandId;
        }
        const { rowItem, rowIndex } = this.getRowAndRowIndex(itemForProductModal.itemId, this.props.items);
        const showTotals = this.props.dimensionsPanelApplied.metrics.showAssortmentTotals;
        const collectionBrandId = collection.getIn(["brand_collection_properties", "struct_value", "brand_id"]);
        const genericAllocationDimensions = getGenericAllocationDimensions(allocationDimensions);

        const allocationDimensionGroups = this.getGroupedAllocationDimensions(
            this.props.allocationDimensions,
            get(this.state.gridParams, GridParamType.RowGroup)
        );
        const dimensionFilters = getNewDimensionFilters(
            this.props.allocationDimensions,
            gridParams,
            this.props.dimensionsPanelApplied
        );

        const allowDuplicateProduct = collection?.getIn(
            CollectionKeys.ITEM_DISPLAY_PROPERTY.ALLOW_DUPLICATE_PRODUCT_ENTRIES
        );
        const dimensionPlanMetrics = this.getActivePlanMetrics();
        const deliveryAllocationDimension = this.getDeliveryAllocationDimension();
        const rawItemDisplayProperty = getRawItemDisplayProperty(this.props.itemDisplayProperty);

        return (
            <div {...productCollectionStyles()}>
                <ProductCollectionBulkActions
                    onValidateItemData={this.getItemsAndValidate}
                    itemsId={itemsId}
                    editable={editable}
                    bulkItems={bulkItems}
                    onBulkItemsClearAll={onBulkItemsClearAll}
                    onOpenDeleteConfirmation={this.handleOpenDeleteConfirmation}
                    gridApi={gridApi}
                    view={view}
                    userRole={userRole}
                    items={items}
                    onSnackbarAdd={onSnackbarAdd}
                    allocatingAssortment={allocatingAssortment}
                    findingSizes={findingSizes}
                    setCopyItemsString={this.handleCopyItemsString}
                    onAllocate={this.handleAllocate}
                    onMatchSelectedSizesWithSizeRanges={this.handleFindSizeRanges}
                    sizingRangeEnabled={sizingRangeEnabled}
                    sizingCurveEnabled={sizingCurveEnabled}
                    sizingAllocationEnabled={sizingAllocationEnabled}
                    pasteAllocation={this.handlePasteAllocation}
                    copiedAllocation={copiedAllocation}
                    onOpenExportCollectionDialog={this.handleOpenExportCollectionDialog}
                    onAcceptPackUnits={this.handleAcceptPackUnits}
                    sizePackEnabled={sizePackEnabled}
                    onOpenExportToMerchantDialog={this.handleOpenExportToMerchantDialog}
                    onSyncInventoryUnits={this.handleSyncInventoryUnits}
                    syncingInventoryUnits={syncingInventoryUnits}
                    isCloseoutAssortmentEnabled={inventoryEnabled}
                    exportOptions={this.props.exportOptions}
                    allocationBagRef={this.props.allocationBagRef}
                    fetchBothITemPO={this.props.fetchBothITemPO}
                />
                <div className="assortmentContainer">
                    <div style={{ width: "100%", display: "flex" }}>
                        <div
                            className={classNames("assortmentMain", {
                                assortmentMainTileView: !isServerRollup && view === View.Tile,
                                toolbarSmall: !isServerRollup && toolpanelOpened && !toolpanelIsBig,
                                toolbarLarge: !isServerRollup && toolpanelOpened && toolpanelIsBig,
                                toolbarCollapsed: !isServerRollup && view === View.Tile && !toolpanelOpened,
                            })}
                        >
                            <ProductCollectionToolbar
                                isServerRollup={isServerRollup}
                                defaultSearchText={defaultSearchText}
                                searchText={searchText}
                                hashtags={hashtags}
                                isMobileOrPhablet={isMobileOrPhablet}
                                isMobileOrPhaTablet={isMobileOrPhaTablet}
                                onClearViewSettings={this.handleClearSortAndFilters}
                                onSearchChange={this.handleSearchChange}
                                onSwitchView={this.handleViewSwitch}
                                view={view}
                                gridParams={gridParams}
                                location={location}
                                onUndo={onUndo}
                                onRedo={onRedo}
                                bulkItems={bulkItems}
                                editable={editable}
                                brandsDataById={brandsDataById}
                                capabilities={capabilities}
                                collectionDriveItem={collectionDriveItem}
                                onToggleAddSMUDialog={onToggleAddSMUDialog}
                                onToggleImageGroups={this.handleToggleImageGroups}
                                collection={collection}
                                collectionExport={collectionExport}
                                collectionId={collectionId}
                                gridApi={gridApi}
                                itemDisplayProperty={itemDisplayProperty}
                                onClearCollectionExport={onClearCollectionExport}
                                onExport={this.handleExportCollection}
                                onCreateNordstromPush={this.handleExportToNordstrom}
                                onDeleteCollection={onDeleteCollection}
                                onSyncProductsStart={onSyncProductsStart}
                                onSyncProductsStop={onSyncProductsStop}
                                onCopy={onCopy}
                                rowDataSet={rowDataSet}
                                syncingProducts={syncingProducts}
                                allocatingAssortment={allocatingAssortment}
                                findingSizes={findingSizes}
                                onClearViewParams={this.handleClearViewParams}
                                onFiltersChange={this.handleToolbarFilterChange}
                                requiredFieldNames={requiredFieldNames}
                                desiredFieldNames={desiredFieldNames}
                                getSuggestionData={this.props.onGetSuggestions}
                                setSuggestionData={this.handlSetSuggestionData}
                                handleFocusOnCellMap={this.handleFocusOnCellMap}
                                onRemovePermission={onRemovePermission}
                                user={user}
                                showTimeline={showTimeline}
                                onToggleTimeline={onToggleTimeline}
                                onToggleTotals={this.handleOnToggleTotals}
                                showTotals={!!showTotals}
                                doorClassificationSchema={doorClassificationSchema}
                                onToggleDoors={this.handleOnToggleDoors}
                                onChangeDoorGrouping={this.handleOnChangeDoorGrouping}
                                doorGrouping={this.getActiveClassification()}
                                onChangeDoorGroupingLabel={this.handleOnChangeDoorGroupingLabel}
                                onChangePlanMetric={this.handleChangePlanMetric}
                                doorGroupingLabel={this.getActiveClassificationLabel()}
                                dimensionPlanMetrics={this.getActivePlanMetrics()}
                                subscription={subscription}
                                onUnsubscribe={onUnsubscribe}
                                onSubscribe={onSubscribe}
                                onAllocateAllRows={this.handleAllocate}
                                onMatchSelectedSizesWithSizeRangesAllRows={this.handleFindSizeRanges}
                                allocationEnabled={allocationEnabled}
                                sizingCurveEnabled={sizingCurveEnabled}
                                sizingRangeEnabled={sizingRangeEnabled}
                                sizePackEnabled={sizePackEnabled}
                                onToggleManageDoorGrouping={this.handleToggleManageDoorGrouping}
                                onToggleManageAllocationDimension={this.handleToggleManageAllocationDimension}
                                onToggleManagePurchaseOrders={this.handleToggleManagePurchaseOrders}
                                allocationDimensions={allocationDimensions}
                                allocationDimensionGroups={allocationDimensionGroups}
                                accessPlan={accessPlan}
                                itemId={itemForProductModal.itemId}
                                merchantId={collection?.getIn(["merchant_v1_id", "string_value"], "") || ""}
                                legacyMerchantId={collection?.getIn(["merchant_id", "string_value"], "") || ""}
                                onOpenProductModal={this.handleOpenProductModalOnItem}
                                showManagePurchaseOrder={merchantPurchaseOrderDoorGroups.length > 0}
                                merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                                deliveryPurchaseOrders={deliveryPurchaseOrders}
                                sizeAllocationType={sizeAllocationType}
                                onOpenExportToMerchant={this.onOpenExportToMerchant}
                                merchantCurrency={merchantCurrency}
                                tileViewData={tileViewData}
                                doorTotals={doorTotals}
                                doorTotalsFiltered={doorTotalsFiltered}
                                uniqueBrandEntities={uniqueBrandEntities}
                                listMerchantCollectionVersionStatutes={listMerchantCollectionVersionStatutes}
                                onAddCollectionTag={onAddCollectionTag}
                                onRemoveCollectionTag={onRemoveCollectionTag}
                                poiTags={poiTags}
                                currentPoi={currentPoi}
                                genericMerchantAllocationDimensions={genericMerchantAllocationDimensions}
                                fetchMerchantPreference={fetchMerchantPreference}
                                fetchMerchantAllocationDimensions={fetchMerchantAllocationDimensions}
                                requestingMerchantAllocationDimensions={requestingMerchantAllocationDimensions}
                                defaultDateFormat={defaultDateFormat}
                                onConfigureTileView={this.handleToggleConfigureTileView}
                                onApplyTileViewTemplate={this.handleApplyTileSettingsTemplate}
                                onResetToDefaultTileView={onResetTileSettings}
                                selectedTileViewKey={selectedTileViewKey}
                                sizePacksTolerance={this.props.sizePacksTolerance}
                                updateCollectionSizePacksTolerance={this.props.updateCollectionSizePacksTolerance}
                                isFetchingBrandsData={isFetchingBrandsData}
                                onFetchBrandsData={onFetchBrandsData}
                                onConfigureTileViewSize={this.handleToggleConfigureTileViewSize}
                                tileViewSize={tileViewSize}
                                merchantFeaturePreference={merchantFeaturePreference}
                                onSyncInventoryUnitsAllRows={this.handleSyncInventoryUnits}
                                syncingInventoryUnits={syncingInventoryUnits}
                                isCloseoutAssortmentEnabled={inventoryEnabled}
                                userMerchantId={userMerchantId}
                                onToggleExpandAllGroups={this.handleToggleExpandAllGroups}
                                expandAllGroups={expandAllGroups}
                                // hideDoorsOptions={this.props.flags?.dimensionsToolPanel ?? false}
                                hideDoorsOptions={false}
                                allocationBagRef={this.props.allocationBagRef}
                                onOpenAssortmentSettings={onOpenCollectionSettings}
                                setScrollToDoorsColumnOnLoad={this.setScrollToDoorsColumnOnLoad}
                                showDoors={this.props.dimensionsPanelApplied.showDoors}
                                dimensionsPanelApplied={this.props.dimensionsPanelApplied}
                                formulaFields={formulaFields}
                                onUpdateCollectionAttribute={onUpdateCollectionAttribute}
                                sizingAllocationEnabled={sizingAllocationEnabled}
                                handleSwitchingTileAndGrid={this.handleSwitchingTileAndGrid}
                                metricsEnabled={this.props.doorGroupMetricsSchema?.size > 0}
                                openDistributionModal={this.props.openDistributionModal}
                                selectedDistribution={this.props.selectedDistribution}
                                setSelectedDistribution={this.props.setSelectedDistribution}
                            />
                            <div className="tileViewMain">
                                {view === View.Tile && !isServerRollup && (
                                    <ProductCollectionTileView
                                        aggregationData={tileViewData.aggData}
                                        allocationBagRef={allocationBagRef}
                                        allowedValueFields={allowedValueFields}
                                        brandEntities={brandEntities}
                                        bulkAddingItems={bulkAddingItems}
                                        cardViewFields={cardViewFields}
                                        doors={doors}
                                        shopViewFields={shopViewFields}
                                        dataEntryType={dataEntryType}
                                        dimensionFilters={dimensionFilters}
                                        editable={editable}
                                        filterAssortmentByBrand={filterByBrand}
                                        filterAssortmentBySMU={filterAssortmentBySMU}
                                        gridApi={gridApi}
                                        gridParams={gridParams}
                                        dimensionsPanelApplied={this.props.dimensionsPanelApplied}
                                        allItems={this.props.tileGridData.data}
                                        items={tileViewData.data}
                                        snapshotItems={items}
                                        groupHeaders={tileViewData.groupHeaders}
                                        itemsId={itemsId}
                                        rowGroup={gridParams?.[GridParamType.RowGroup]}
                                        schema={schemaFields}
                                        searchableFields={searchableFields}
                                        searchTerm={searchText}
                                        onLinkProductToItem={this.handleShowLinkDialog}
                                        onMoveItem={onMoveItem}
                                        onOpenProductModal={this.handleOpenProductModalOnItem}
                                        onOpenDeleteConfirmation={this.handleOpenDeleteConfirmation}
                                        onAddItem={onAddItem}
                                        collectionId={collectionId}
                                        formulaFields={formulaFields}
                                        allocationDimensions={allocationDimensions}
                                        allowDuplicateProduct={allowDuplicateProduct}
                                        merchantCurrency={merchantCurrency}
                                        tileSettings={tileSettings}
                                        rawItemDisplayProperty={rawItemDisplayProperty}
                                        tileViewSize={tileViewSize}
                                    />
                                )}
                                <GridSplit
                                    isGridOpen={this.props.isFinancialTargetGridOpen}
                                    shouldRenderTargetsGrid={
                                        this.props.flags.financialTarget && this.state.view !== View.Tile
                                    }
                                    financialTargetGrid={
                                        gridApi &&
                                        columnApi &&
                                        allocationBagRef &&
                                        this.props.isFinancialTargetGridOpen && (
                                            <FinancialTargetGrid
                                                connectedGrid={{
                                                    ref: this.state.mainGridRef,
                                                    gridViewRef: this.gridViewRef,
                                                    columnApi,
                                                    gridApi,
                                                    columnsDefs: gridApi.getColumnDefs(),
                                                }}
                                                ref={this.targetMetricsGridRef}
                                                showDoors={this.props.dimensionsPanelApplied.showDoors}
                                                toggleDoors={this.handleOnToggleDoors}
                                                schema={this.props.itemDisplayProperty}
                                                merchantId={collection.getIn(["merchant_v1_id", "string_value"], "")}
                                                targetGroupsWithTargets={this.props.targetGroupsWithTargets}
                                                targetBag={this.props.targetBag}
                                                selectedTargetIds={this.props.selectedTargetIds}
                                                displayedRows={this.props.displayedFinancialTargetGridRows}
                                                isCollapseModeOn={this.props.isCollapseModeOn}
                                                metrics={this.props.dimensionsPanelApplied.metrics}
                                            />
                                        )
                                    }
                                >
                                    <ProductCollectionGridView
                                        collectionDriveItem={collectionDriveItem}
                                        onFetchCollectionDriveItem={onFetchCollectionDriveItem}
                                        onStartCreateRollupSnapshot={onStartCreateRollupSnapshot}
                                        allocationDimensionGroups={allocationDimensionGroups}
                                        allocationDimensions={genericAllocationDimensions}
                                        recreateServerRollupSnapshot={recreateServerRollupSnapshot}
                                        isAllocationDimensionModalOpen={manageAllocationDimensionOpen}
                                        doors={doors}
                                        dates={dates}
                                        shopViewFields={shopViewFields}
                                        doorClassificationSchema={doorClassificationSchema}
                                        dataEntryType={dataEntryType}
                                        onColumnGroupOpened={this.handleColumnGroupOpened}
                                        onMutateCollectionBudget={onMutateCollectionBudget}
                                        onMutateReviewPlan={onMutateReviewPlan}
                                        onMutateMetricsReviewPlan={onMutateMetricsReviewPlan}
                                        itemDisplayProperty={itemDisplayProperty}
                                        allocationEnabled={allocationEnabled}
                                        // Used for allocation copy and paste right click
                                        sizingAllocationEnabled={sizingAllocationEnabled}
                                        sizePackEnabled={sizePackEnabled}
                                        currentViewName={currentViewName}
                                        currentAllocationDimensionView={currentAllocationDimensionView}
                                        customFilteredItems={customFilteredItems}
                                        collectionId={collectionId}
                                        collection={collection}
                                        capabilities={capabilities}
                                        items={items}
                                        itemsId={itemsId}
                                        onMutateCollectionItem={onMutateCollectionItem}
                                        onMutateDefaultItem={onMutateDefaultItem}
                                        onAddItem={onAddItem}
                                        onOpenDeleteConfirmation={this.handleOpenDeleteConfirmation}
                                        onMoveItem={onMoveItem}
                                        onBulkItemsSet={onBulkItemsSet}
                                        onGridMount={this.handleGridMount}
                                        onUpdateCollectionAttribute={onUpdateCollectionAttribute}
                                        onFirstDataRendered={this.onFirstDataRendered}
                                        onToolPanelVisibleChanged={this.handleToolPanelVisibleChanged}
                                        isResetColumns={this.state.isResetColumns}
                                        onResetColumns={this.handleResetColumns}
                                        searchText={searchText}
                                        onSearchChange={this.handleForceSearchChange}
                                        hashtags={hashtags}
                                        scrollToIndex={scrollToIndex}
                                        router={router}
                                        location={location}
                                        isResizing={false}
                                        bulkItems={bulkItems}
                                        onFocusChange={onFocusChange}
                                        focusOnCellMap={focusOnCellMap}
                                        onFocusOnCellMap={this.handleFocusOnCellMap}
                                        onOpenProductModal={this.handleOpenProductModalOnItem}
                                        sort={gridParams && gridParams[GridParamType.Sort]}
                                        isDraggable={!!(gridParams && gridParams[GridParamType.Sort])}
                                        rowGroup={gridParams && gridParams[GridParamType.RowGroup]}
                                        filter={gridParams && gridParams[GridParamType.Filter]}
                                        aggFunc={gridParams && gridParams[GridParamType.AggFunc]}
                                        pivot={gridParams && gridParams[GridParamType.Pivot]}
                                        onGridParamsChange={this.handleGridParamsChange}
                                        onViewParamsChange={this.handleViewParamChange}
                                        onClearViewParams={this.handleClearViewParams}
                                        handleOnChangeDoorGroupingLabel={this.handleOnChangeDoorGroupingLabel}
                                        onChangeDefaultItemVisibility={
                                            this.props.defaultItem && this.handleShowDefaultItemRow
                                        }
                                        showDefaultItem={showDefaultItem}
                                        onRowDataSet={this.handleRowDataSet}
                                        gridParams={gridParams}
                                        editable={editable}
                                        onSubscribeToItemsUpdates={this.props.onSubscribeToItemsUpdates}
                                        onUnsubscribeToItemsUpdates={this.props.onUnsubscribeToItemsUpdates}
                                        onLinkProductToItem={this.handleShowLinkDialog}
                                        filterAssortmentByBrand={filterByBrand}
                                        filterAssortmentBySMU={filterAssortmentBySMU}
                                        bulkAddingItems={bulkAddingItems}
                                        allowedValueFields={allowedValueFields}
                                        view={view}
                                        onSetTileViewData={onSetTileViewData}
                                        onFilterAssortmentBySMU={this.handleFilterAssortmentBySMU}
                                        copiedAllocation={copiedAllocation}
                                        copiedAllocationName={copiedAllocationName}
                                        copiedAllocationSizes={copiedAllocationSizes}
                                        copyAllocation={this.handleCopyAllocation}
                                        pasteAllocation={this.handlePasteAllocation}
                                        onSnackbarAdd={onSnackbarAdd}
                                        onChangeCurrentView={onChangeCurrentView}
                                        budgets={budgets}
                                        brandEntities={brandEntities}
                                        collectionBudgetActuals={collectionBudgetActuals}
                                        copiedDoorAllocationName={copiedDoorAllocationName}
                                        onPasteDoorAllocations={onPasteDoorAllocations}
                                        onPasteItems={this.handlePasteItems}
                                        setCopyItemsString={this.handleCopyItemsString}
                                        getSuggestionData={this.props.onGetSuggestions}
                                        setSuggestionData={this.handlSetSuggestionData}
                                        onFilterChanged={this.handleFilterChanged}
                                        showTotals={showTotals}
                                        copyItemsString={copyItemsString}
                                        detailFields={detailFields}
                                        toolpanelOpened={toolpanelOpened}
                                        onClearAllocations={onClearAllocations}
                                        sizeRanges={sizeRanges}
                                        sizePacks={sizePacks}
                                        onResetSizeRanges={onResetSizeRanges}
                                        onLookupAssortmentErrors={onLookupAssortmentErrors}
                                        dimensionPlanMetrics={dimensionPlanMetrics}
                                        reviewPlanRows={reviewPlanRows}
                                        reviewPlan={reviewPlan}
                                        accessPlan={accessPlan}
                                        isRollupMode={isRollupMode}
                                        rollupType={rollupType}
                                        onExportToExcel={onExportToExcel}
                                        syncingReviewPlans={syncingReviewPlans}
                                        onFetchPinnedRows={onFetchPinnedRows}
                                        doorTotals={doorTotalsFiltered}
                                        doorTotalRowFiltered={doorTotalRowFiltered}
                                        merchantId={collection?.getIn(["merchant_v1_id", "string_value"], "") || ""}
                                        legacyMerchantId={collection?.getIn(["merchant_id", "string_value"], "") || ""}
                                        sizeTableType={sizeTableType}
                                        merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                                        allowDuplicateProduct={allowDuplicateProduct}
                                        merchantCurrency={merchantCurrency}
                                        onPinnedRowOrder={this.onPinnedRowOrder}
                                        pinnedRowColumnOrder={pinnedRowColumnOrder}
                                        poiToItemsToSizeBulksMap={poiToItemsToSizeBulksMap}
                                        onFetchCollectionPOI={onFetchCollectionPOI}
                                        defaultDateFormat={defaultDateFormat}
                                        defaultItem={this.props.defaultItem}
                                        canShopBrand={canShopBrand}
                                        onSetDoorTotals={onSetDoorTotals}
                                        onSetFilteredDoorTotals={onSetFilteredDoorTotals}
                                        onSetDoorTotalRow={onSetDoorTotalRow}
                                        tileGridContext={tileGridContext}
                                        onSetImageCellDimensions={onSetImageCellDimensions}
                                        imageCellDimensions={imageCellDimensions}
                                        activeValidationMode={activeValidationMode}
                                        itemsToBeValidated={itemsToBeValidated}
                                        errorLog={errorLog}
                                        isServerRollup={isServerRollup}
                                        expandAllGroups={expandAllGroups}
                                        flags={this.props.flags}
                                        onEditDoorGroupingRequest={this.handleToggleManageAllocationDimension}
                                        onImportDoorAttributes={this.handleToggleImportDoorAttributesDialog}
                                        onMutateBagValues={this.props.onMutateBagValues}
                                        allocationBagRef={allocationBagRef}
                                        scrollToDoorsColumnOnLoad={scrollToDoorsColumnOnLoad}
                                        setScrollToDoorsColumnOnLoad={this.setScrollToDoorsColumnOnLoad}
                                        startingMutationCount={startingMutationCount}
                                        dimensionsPanelApplied={this.props.dimensionsPanelApplied}
                                        itemsRevision={itemsRevision}
                                        fetchSavedViewData={this.fetechSavedViewData}
                                        showTooManyOperationsDialog={showTooManyOperationsDialog}
                                        merchantAllocationDimensions={merchantAllocationDimensions}
                                        getAlignedGrids={this.getAlignedGrids}
                                        switchingTile={this.state.switchingTile}
                                        handleSwitchingTileAndGrid={this.handleSwitchingTileAndGrid}
                                        metricsEnabled={this.props.doorGroupMetricsSchema?.size > 0}
                                        distributionTargets={this.props.distributionTargets}
                                        isCollapseModeOn={this.props.isCollapseModeOn}
                                        selectedTargetIds={this.props.selectedTargetIds}
                                        ref={this.gridViewRef}
                                    />
                                </GridSplit>
                            </div>
                        </div>
                    </div>
                </div>
                {gridApi && itemForProductModal.itemId && (
                    <ProductCollectionItemModal
                        itemId={itemForProductModal.itemId}
                        collectionId={collectionId}
                        itemCollectionId={this.props.location.query.collectionId}
                        schema={detailViewFields}
                        onCreateCollectionAsset={onCreateCollectionAsset}
                        onMutateCollectionBudget={onMutateCollectionBudget}
                        onMutateCollectionItem={onMutateCollectionItem}
                        onClearAllocations={onClearAllocations}
                        onLookupAssortmentErrors={onLookupAssortmentErrors}
                        isCollectionUploading={isCollectionUploading}
                        itemDisplayProperty={itemDisplayProperty}
                        items={items}
                        itemsId={itemsId}
                        onRequestClose={this.handleCloseProductModal}
                        onRequestCloseWithConflict={this.handleCloseProductModalWithConflict}
                        hashtags={hashtags}
                        row={rowItem}
                        rowIndex={rowIndex}
                        editable={editable}
                        currentAllocationDimensionView={currentAllocationDimensionView}
                        onLinkProductToItem={this.handleShowLinkDialog}
                        onOpenCloneConfirmation={this.handleOpenCloneConfirmation}
                        onOpenDeleteConfirmation={this.handleOpenDeleteConfirmation}
                        allowedValueFields={allowedValueFields}
                        router={router}
                        onOpenProductModalOnProduct={this.handleOpenProductModalOnProduct}
                        copiedAllocation={copiedAllocation}
                        copiedAllocationName={copiedAllocationName}
                        copiedAllocationSizes={copiedAllocationSizes}
                        copyAllocation={this.handleCopyAllocation}
                        pasteAllocation={this.handlePasteAllocation}
                        onSnackbarAdd={onSnackbarAdd}
                        brandEntities={brandEntities}
                        detailFields={detailFields}
                        dates={dates}
                        doors={doors}
                        doorClassificationSchema={doorClassificationSchema}
                        dimensionsPanelApplied={this.props.dimensionsPanelApplied}
                        gridParams={gridParams}
                        onColumnGroupOpened={this.handleColumnGroupOpened}
                        allocationEnabled={allocationEnabled}
                        sizingAllocationEnabled={sizingAllocationEnabled}
                        sizingCurveEnabled={sizingCurveEnabled}
                        sizingRangeEnabled={sizingRangeEnabled}
                        isRollupMode={isRollupMode}
                        allocatingAssortment={allocatingAssortment}
                        findingSizes={findingSizes}
                        onAllocate={this.handleAllocate}
                        onMatchSelectedSizesWithSizeRanges={this.handleFindSizeRanges}
                        allocationDimensions={genericAllocationDimensions}
                        itemsRevision={itemsRevision}
                        orderedItems={tileViewData.data}
                        onOpenProductModal={this.handleOpenProductModalOnItem}
                        merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                        merchantFeaturePreference={merchantFeaturePreference}
                        sizeTableType={sizeTableType}
                        merchantCurrency={merchantCurrency}
                        poiToItemsToSizeBulksMap={poiToItemsToSizeBulksMap}
                        onFetchCollectionPOI={onFetchCollectionPOI}
                        sizePacks={sizePacks}
                        sizePackEnabled={sizePackEnabled}
                        canShopBrand={canShopBrand}
                        onAcceptPackUnits={this.handleAcceptPackUnits}
                        snapshotItems={snapshotItems}
                        isServerRollup={isServerRollup}
                        gridApi={gridApi}
                        onSetServerRollupSnapshotItem={onSetServerRollupSnapshotItem}
                        onMutateBagValues={this.props.onMutateBagValues}
                        allocationBagRef={allocationBagRef}
                        assortmentDimensionMetrics={this.props.doorGroupMetricsSchema?.size > 0}
                        nodeRowIndex={this.state.nodeRowIndex}
                        rootGridApi={gridApi}
                    />
                )}
                {this.state.exportDialogOpen && (
                    <ProductCollectionExportDialog
                        gridApi={gridApi}
                        allocationDimensions={allocationDimensions}
                        collectionExport={collectionExport}
                        onClose={this.handleCloseExportCollectionDialog}
                        onExport={this.handlePrepareDataToExportCollection}
                        isCloseoutAssortmentEnabled={inventoryEnabled}
                    />
                )}
                <AlertDialog
                    open={notifyItemModalConflict}
                    onRequestClose={this.handleAcknowledgeConflict}
                    actionLabel={formatMessage("Close")}
                    title={formatMessage("Conflict")}
                >
                    {formatMessage(
                        "Oops! It seems the product you were trying to view has been deleted by another user."
                    )}
                </AlertDialog>
                {itemIdToLink && (
                    <LinkProduct
                        merchantId={collection.getIn(["merchant_v1_id", "string_value"], "")}
                        brandId={collectionBrandId ? collectionBrandId : this.props.brandId}
                        isFetching={isFetchingModal}
                        globalProducts={globalProducts}
                        globalProductState={globalProductState}
                        onCancel={this.handleCancelLinkProductToItem}
                        onFetchGlobalProducts={onFetchGlobalProducts}
                        onFetchNextGlobalProducts={onFetchNextGlobalProducts}
                        onResetGlobalProducts={onResetGlobalProducts}
                        onSubmit={this.handleLinkProductToItem}
                        cardViewFields={cardViewFields}
                        setViewFullDetailProductId={this.setViewFullDetailProductId}
                        handleOpenIsViewFullDetail={this.handleOpenIsViewFullDetail}
                        itemDisplayProperty={itemDisplayProperty}
                    />
                )}
                {this.state.exportToMerchantDialogOpen && (
                    <ProductCollectionExportToMerchantModal
                        merchantAllocationDimensions={genericAllocationDimensions}
                        merchantId={collection?.getIn(["merchant_v1_id", "string_value"], "") || ""}
                        fetchMerchantAllocationDimensions={fetchMerchantAllocationDimensions}
                        onConfirmClose={this.handleCloseExportToMerchantDialog}
                        exportToMerchantDialogOpen={this.state.exportToMerchantDialogOpen}
                        gridApi={gridApi}
                        onBulkItemsClearAll={onBulkItemsClearAll}
                        collectionId={collectionId}
                        merchantFeaturePreference={merchantFeaturePreference}
                        onCreateCollectionExportItemsPO={onCreateCollectionExportItemsPO}
                        onExportItemsCollection={this.handleExportCollectionItems}
                        onValidateItems={this.getItemsAndValidate}
                        exportOptions={this.props.exportOptions}
                        hasExportLogs={this.props.hasExportLogs}
                        exportItemPO={this.props.exportItemPO}
                        onFetchItemPO={this.fetchItemPO}
                        onFetchMoreRows={this.props.onFetchMoreRows}
                    />
                )}
                {this.state.exportWithWarningsDialogOpen && (
                    <ExportWithWarningsDialog
                        title={formatMessage("We found some issues")}
                        onRequestClose={this.handleCloseExportWithWarningDialog}
                        open={this.state.exportWithWarningsDialogOpen}
                        onViewErrorLog={this.handleViewErrorLog}
                        desiredFieldNames={this.state.desiredFields}
                        itemDisplayProperty={itemDisplayProperty}
                        bypassDesiredConditions={collectionDriveItem?.capabilities?.can_share}
                        desiredFieldsEmpty={this.state.desiredFields?.length === 0}
                        onCreateCollectionExportItemsPO={onCreateCollectionExportItemsPO}
                        exportItemsCollection={this.state.exportItemsCollection}
                        onValidateItemData={this.getItemsAndValidate}
                        onBulkItemsClearAll={onBulkItemsClearAll}
                    />
                )}
                {isViewFullDetailOpen && (
                    <ProductDetailsModal
                        merchantId={collection.getIn(["merchant_v1_id", "string_value"], "")}
                        onRequestClose={this.handleCloseIsViewFullDetail}
                        modalOpen={isViewFullDetailOpen}
                        productId={viewFullDetailProductId}
                        onFetchGlobalProducts={onFetchGlobalProducts}
                        globalProducts={globalProducts}
                        onSelectColorVariant={this.onSelectColorVariant}
                        router={router}
                        onAddItem={onAddItem}
                        itemDisplayProperty={detailDisplayFields}
                        brandEntities={brandEntities}
                        items={items}
                        editable={false}
                        readOnly={true}
                        shopViewFields={shopViewFields}
                    />
                )}
                <Dialog
                    title={formatMessage("Delete Products")}
                    style={{ zIndex: "9999" }}
                    modal={false}
                    onRequestClose={this.handleCloseDeleteConfirmation}
                    open={this.state.isDeleteConfirmationOpen}
                    contentStyle={{ maxWidth: "360px" }}
                    actions={[
                        <Button
                            key="cancel"
                            label={formatMessage("Cancel")}
                            onClick={this.handleCloseDeleteConfirmation}
                        />,
                        <Button
                            data-test={DATATEST.ASSORTMENTS.DIALOG.DELETE}
                            key="delete"
                            label={formatMessage("Delete")}
                            labelStyle={{ color: "white" }}
                            style={{
                                marginLeft: "24px",
                                backgroundColor: rawTheme.palette.errorColor,
                            }}
                            onClick={this.handleDeleteItems}
                        />,
                    ]}
                >
                    {formatMessage("Are you sure you want to delete product(s) from your assortment?")}
                </Dialog>
                <Dialog
                    title={formatMessage("Clone Product")}
                    style={{ zIndex: "9999" }}
                    modal={false}
                    onRequestClose={this.handleCloseCloneConfirmation}
                    open={this.state.isCloneConfirmationOpen}
                    contentStyle={{ maxWidth: "360px" }}
                    actions={[
                        <Button
                            key="cancel"
                            label={formatMessage("Cancel")}
                            onClick={this.handleCloseCloneConfirmation}
                        />,
                        <Button
                            data-test={DATATEST.ASSORTMENTS.DIALOG.CLONE}
                            key="clone"
                            label={formatMessage("Clone")}
                            labelStyle={{ color: "white" }}
                            style={{
                                marginLeft: "24px",
                                backgroundColor: rawTheme.palette.primary1Color,
                            }}
                            onClick={this.handleCloneItem}
                        />,
                    ]}
                >
                    {formatMessage("Are you sure you want to clone this product?")}
                </Dialog>
                {manageDoorGroupingOpen && (
                    <ManageDoorGrouping
                        open={manageDoorGroupingOpen}
                        onToggleManageDoorGrouping={this.handleToggleManageDoorGrouping}
                        doorClassificationSchema={getFilteredClassificationSchema(
                            doorClassificationSchema,
                            doors,
                            merchantPurchaseOrderDoorGroups
                        )}
                        doorGrouping={this.getActiveClassification()}
                        groupingClassificationHide={this.getGroupingClassificationHide()}
                        groupingClassificationOrder={this.getGroupingClassificationOrder()}
                        dimensionsPanelApplied={this.props.dimensionsPanelApplied}
                        onApply={this.handleDimensionsStateChange}
                        onChangeCurrentView={onChangeCurrentView}
                        itemDisplayProperty={itemDisplayProperty}
                        merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                    />
                )}
                {manageAllocationDimensionOpen && genericAllocationDimensions.length > 0 && (
                    <UploaderProvider
                        doorsDimension={genericAllocationDimensions[genericAllocationDimensions.length - 1]}
                        onCustomDoorCellValueChanged={onCustomDoorCellValueChanged}
                        handleToggleImportDoorAttributesDialog={this.handleToggleImportDoorAttributesDialog}
                        handleToggleManageAllocationDimension={this.handleToggleManageAllocationDimension}
                    >
                        <ManageAllocationDimension
                            isOpenManageAllocationDimension={manageAllocationDimensionOpen}
                            onToggleShow={this.handleToggleManageAllocationDimension}
                            doorsDimension={genericAllocationDimensions[genericAllocationDimensions.length - 1]}
                            onMutateAllocationDimensionValue={this.handleMutateAllocationDimensionValue}
                            onMoveAllocationDimensionValue={onMoveAllocationDimensionValue}
                            isSaving={syncingAllocationDimension}
                            readOnly={!editable}
                            merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                            unitsEntryType={dataEntryType}
                            onPurchaseOrdersError={this.handleNoPurchaseOrders}
                            isRollupMode={isRollupMode}
                            doorClassificationSchema={getFilteredClassificationSchema(
                                doorClassificationSchema,
                                doors,
                                merchantPurchaseOrderDoorGroups
                            )}
                            onCustomDoorCellValueChanged={onCustomDoorCellValueChanged}
                            updateAllocationDimensionAttribute={updateAllocationDimensionAttribute}
                            collectionsRefs={collection.getIn(["collections_refs"])}
                            assortmentName={nameExtRemover(
                                collectionDriveItem?.name,
                                collectionDriveItem.kind as ItemKind
                            )}
                            manageAllocationDimensionTab={manageAllocationDimensionTab}
                        />

                        <DialogDoubleCheckChanges />
                    </UploaderProvider>
                )}
                {managePurchaseOrdersOpen && deliveryAllocationDimension && (
                    <ManageDeliveryPurchaseOrder
                        onToggleShow={this.handleToggleManagePurchaseOrders}
                        parentAllocationDimension={deliveryAllocationDimension}
                        mutateDeliveryPurchaseOrder={this.handleUpdateDeliveryPurchaseOrder}
                        readOnly={!editable}
                        deliveryPurchaseOrders={deliveryPurchaseOrders}
                        merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                        validatePOTable={validatePOTable}
                    />
                )}
                {isConfigureTileViewOpen && (
                    <ManageTileView
                        itemDisplayProperty={itemDisplayProperty}
                        onConfirm={this.handleApplyNewTileSettings}
                        onClose={this.handleToggleConfigureTileView}
                        initialTileSettings={tileSettings}
                        hasAllocationDimensions={!!allocationDimensions.length}
                    />
                )}
                {/* prevent to show Toaster when soft Warnings Dialog is open */}
                {!this.state.exportWithWarningsDialogOpen && (
                    <ProductGenericToaster open={!!validationOutcome} onClose={this.handleValidationOutcomeClose}>
                        <div className="validationSummary">
                            {validationOutcome?.[0] > 0 && (
                                <h4>
                                    {formatMessage("|products| product(s) validated", {
                                        products: validationOutcome[0],
                                    })}
                                </h4>
                            )}
                            {validationOutcome?.[1] > 0 && (
                                <h4>
                                    {formatMessage("|products| product(s) contain(s) warnings", {
                                        products: validationOutcome[1],
                                    })}
                                </h4>
                            )}
                            {validationOutcome?.[2] > 0 && (
                                <h4>
                                    {formatMessage("|products| product(s) contain(s) errors", {
                                        products: validationOutcome[2],
                                    })}
                                </h4>
                            )}
                            {validationOutcome?.[3] > 0 && (
                                <h4>{formatMessage("Assortment contains additional error")}</h4>
                            )}
                        </div>
                        {validationOutcome?.slice(1)?.some((item) => item > 0) && (
                            <div className="validationActions">
                                <button onClick={this.handleViewErrorLog}>{formatMessage("View Error Log")}</button>
                            </div>
                        )}
                    </ProductGenericToaster>
                )}
            </div>
        );
    }

    private handleAllocate = (action: CollectionAction, ids: string[] = []) => {
        if (!Array.isArray(ids) || !ids.length) {
            ids = [];
            this.props.gridApi.forEachNode((node) => {
                // Only allocate row that is NOT proxy OR first proxy (index=0) to prevent duplicate
                if (node.data && !node.data.proxyIndex) {
                    ids.push(node.data.fields.id.string_value);
                }
            });
        }

        const merchantId = this.props.collection.getIn(["merchant_v1_id", "string_value"], "");
        const rawItemDisplayProperty = getRawItemDisplayProperty(this.props.itemDisplayProperty);
        setTimeout(() => this.props.onAllocate(action, ids, rawItemDisplayProperty, merchantId), MENU_HIDE);
    };

    private handleSyncInventoryUnits = (action: CollectionAction, ids: string[] = []) => {
        if (!Array.isArray(ids) || !ids.length) {
            ids = [];
            this.props.gridApi.forEachNode((node) => {
                // Only sync row that is NOT proxy OR first proxy (index=0) to prevent duplicate
                if (node.data && !node.data.proxyIndex) {
                    ids.push(node.data.fields.id.string_value);
                }
            });
        }

        const { onSyncInventoryUnits } = this.props;
        onSyncInventoryUnits(action, ids);
    };

    private handleAcceptPackUnits = () => {
        const { gridApi, onClearAssortmentError, items } = this.props;
        const { rowItem } = this.getRowAndRowIndex(this.state.itemForProductModal.itemId, items);
        if (rowItem) {
            const rowId = amphibiousGetIn(rowItem, Item.FIELDS.ID);
            this.props.syncBulkWithAllocation(rowId);
        } else {
            gridApi.getSelectedRows().forEach((item) => {
                this.props.syncBulkWithAllocation(amphibiousGetIn(item, Item.FIELDS.ID));
                onClearAssortmentError();
            });
        }
    };

    private handleFindSizeRanges = (action: CollectionAction, ids: string[] = []) => {
        const filterByBrand: string = this.props.brandId;
        if (!Array.isArray(ids) || !ids.length) {
            ids = [];
            this.props.gridApi.forEachNode((node) => {
                // Only allocate row that is NOT proxy OR first proxy (index=0) to prevent duplicate
                if (node.data && !node.data.proxyIndex) {
                    ids.push(node.data.fields.id.string_value);
                }
            });
        }
        this.props.onMatchSelectedSizesWithSizeRanges(action, ids, filterByBrand);
    };

    private handleOpenExportCollectionDialog = () => {
        this.setState({ exportDialogOpen: true });
    };

    private handleCloseExportCollectionDialog = () => {
        this.setState({ exportDialogOpen: false });
        this.props.onClearCollectionExport();
    };

    private handlePrepareDataToExportCollection = (exportParams: IExportParams) => {
        const { collectionDriveItem } = this.props;
        const collectionName = nameExtRemover(collectionDriveItem?.name, ItemKind.CollectionDocument);

        this.handleExportCollection({
            ...exportParams,
            exportFileName: collectionName,
        });
    };

    private handleOpenExportToMerchantDialog = () => {
        this.setState({ exportToMerchantDialogOpen: true });
    };

    private handleCloseExportToMerchantDialog = () => {
        this.setState({ exportToMerchantDialogOpen: false });
    };

    private handleCloseExportWithWarningDialog = () => {
        this.setState({ exportWithWarningsDialogOpen: false });
    };

    private handleExportCollectionItems = (exportItemsCollection) => {
        this.setState({ exportItemsCollection, exportWithWarningsDialogOpen: true });
    };

    private getCartView = () => {
        const { location, me } = this.props;
        const { cartView } = location.query;
        return cartView || me.last_assortment_view || View.Grid;
    };

    private handleExportCollection = (exportParams: IExportParams) => {
        const {
            collection,
            collectionId,
            onCreateCollectionExport,
            gridApi,
            columnApi,
            doorTotals,
            inventoryEnabled,
            itemDisplayProperty,
            defaultDateFormat,
        } = this.props;
        const merchantName = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.MERCHANT_NAME);

        if (this.props.isServerRollup) {
            const numberOfRows = doorTotals?._total?.totalRows ?? 0;
            const { exportOnlyVisibleColumns, withImages } = exportParams;
            const allRows = exportOnlyVisibleColumns === ExportCollectionSelectedSetting.All;
            const columns = (allRows ? columnApi.getAllColumns() : columnApi.getAllDisplayedColumns()).filter(
                (column: Column) => column.getColId?.()
            );

            let visibleColumnsList = columns.map((c) => c.getColId?.()).filter((c) => !!c);
            if (!withImages) {
                visibleColumnsList = visibleColumnsList.filter((c) => c !== IMAGE_URL);
            }

            const queryFilter = allRows
                ? null
                : buildExportSnapshotQuery({
                      filterModel: gridApi?.getFilterModel(),
                      itemDisplayProperty,
                      searchTokens: (this.state.searchText && this.state.searchText.toUpperCase().split(" ")) || [],
                      merchantDateFormat: defaultDateFormat,
                      filterAssortmentBySMU: this.state.filterAssortmentBySMU,
                      dimensionFilters: getDimensionFilters(this.state.gridParams),
                  });

            onCreateCollectionExport({
                ...exportParams,
                merchantName,
                collectionId,
                visibleColumnsList,
                rollupSnapshotId: collectionId,
                numberOfRows,
                queryFilter,
            });
        } else {
            // Export assortment
            prepareDataToExportCollection({
                ...exportParams,
                collectionId,
                gridApi,
                onCreateCollectionExport,
                merchantName,
                view: this.getCartView(),
                inventoryEnabled,
            });
        }
    };

    private handleExportToNordstrom = (exportConfig) => {
        const { collection, me, onCreateNordstromPush } = this.props;
        const merchantName = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.MERCHANT_NAME);

        onCreateNordstromPush({
            ...exportConfig,
            merchantName,
            view: this.getCartView(),
            accountType: me.account_type,
        });
    };

    /**
     * Allow us to pass grid api around since it isnt react and some things are not state based
     */

    private handleGridMount = (gridApi, columnApi, mainGridRef) => {
        this.props.onGridMount(gridApi, columnApi);
        this.setState({
            mainGridRef,
        });
    };

    private handleRowDataSet = () => {
        this.setState({ rowDataSet: true });
    };

    private handleCloseProductModal = () => {
        this.updateLocationQuery({
            itemId: undefined,
            collectionId: undefined,
            productId: undefined,
        });
    };

    private handleCloseProductModalWithConflict = () => {
        this.setState({ notifyItemModalConflict: true });
        this.updateLocationQuery({
            itemId: undefined,
            collectionId: undefined,
            productId: undefined,
        });
    };

    private handleToggleExpandAllGroups = () => {
        this.setState({ expandAllGroups: !this.state.expandAllGroups });
        this.updateLocationQuery({
            expand: this.state.expandAllGroups ? "false" : "true",
        });
    };

    private handleAcknowledgeConflict = () => {
        this.setState({ notifyItemModalConflict: false });
    };

    private handleOpenProductModalOnItem = (id: string, assortmentId?: string, nodeRowIndex?: number) => {
        SentryUtil.startTransaction(AssortmentTransactions.OpenCDP);
        this.updateLocationQuery({ itemId: id, collectionId: assortmentId || undefined, productId: undefined });

        // Validate that nodeRowIndex is greater than `0`
        if (nodeRowIndex >= 0) {
            this.setState({ nodeRowIndex });
        }
    };

    private handleOpenProductModalOnProduct = (productId: string) => {
        const { itemForProductModal } = this.state;
        this.updateLocationQuery({
            itemId: itemForProductModal.itemId,
            productId,
        });
    };

    // Memoize this function so it won't return new object that breaks shallow comparison
    private parseProductModalParams = memoizeOne((productId?: string, itemId?: string) => {
        const itemForProductModal: Partial<IItemForProductModal> = {};
        if (productId || itemId) {
            itemForProductModal.itemId = itemId;
            itemForProductModal.productId = productId;
        }
        return { itemForProductModal };
    });

    private handleOpenCloneConfirmation = () => {
        this.setState({ isCloneConfirmationOpen: true });
    };

    private handleCloseCloneConfirmation = () => {
        this.setState({ isCloneConfirmationOpen: false });
    };

    private handleCopyItemsFromOtherTab = (event) => {
        if (event.key === ASSORTMENT_COPY_ITEMS) {
            const itemState = this.state.copyItemsString;
            if (itemState !== event.newValue && event.newValue !== "") {
                this.handleCopyItemsString(event.newValue);
            }
        }
    };
    private handlePasteItems = () => {
        const {
            items,
            itemsId,
            onAddItem,
            collection,
            collectionId,
            onSnackbarAdd,
            dates,
            doors,
            allocationBagRef,
            onMutateBagValues,
        } = this.props;
        const { copyItemsString } = this.state;

        // Guard statement to avoid nested blocks
        if (copyItemsString === "") {
            return;
        }

        let itemsToCopy = [];
        let allocations;
        try {
            itemsToCopy = JSON.parse(copyItemsString);
            allocations = JSON.parse(localStorage.getItem(ASSORTMENT_COPY_ALLOCATIONS));
        } catch (e) {
            // leave empty
        }

        const collectionBrandId = collection.getIn(["brand_collection_properties", "struct_value", "brand_id"]);
        const collectionBrandName = collection.getIn(["brand_collection_properties", "struct_value", "brand_name"]);

        const mismatchedItems = itemsToCopy.filter(
            (itemToCopy) => get(itemToCopy, "fields._brand_id.string_value") !== collectionBrandId
        );

        const validDates = Set(dates?.map((d) => d.id) || []);
        const validDoors = Set(doors?.map((d) => d.id) || []);

        /**
         * This seciton handles copy/paste for:
         * 1. items
         * 2. allocations & bulks associated with items
         *
         * - Pasted items are assigned new unique IDs
         * - Does not copy any delivery/door if delivery/door does not exist in the target assortment
         * - Copy/paste works for both old and new allocation bag:
         *   - Old -> New - Works
         *   - New -> New - Works
         *   - New -> Old - Not implemented
         * - When pasting from Old to New, allocation and bulks fields of the item object are removed
         * - After pasting the clipboard for allocation/bulk is cleared
         */
        cleanItemsbyDoorsAndDates(itemsToCopy, dates, doors)
            .reverse()
            .filter((item) => !collectionBrandId || get(item, "fields._brand_id.string_value") === collectionBrandId)
            .forEach((item) => {
                const itemId = get(item, "fields.id.string_value");
                const newId = uuid();
                let newItem = setIn(item, "fields.id.string_value", newId);

                if (allocationBagRef) {
                    // New to New
                    const bagTypes = Object.values(AllocationBagType).filter((v) => typeof v === "number");
                    bagTypes.forEach((bagType: AllocationBagType) => {
                        const bagKey = [itemId, bagType].join(BAG_VALUE_PARAMS_SEPARATOR);
                        const allocationBag = get(allocations, `[${bagKey}].map.fields`, {});
                        Object.entries(allocationBag).forEach(([key, value]) => {
                            const dimensionValues = key.split(BAG_VALUE_PARAMS_SEPARATOR);
                            const quantity = +get(value, "string_value", 0);
                            if (dates) {
                                const [date, door] = dimensionValues;
                                if (!validDates.has(date) || !validDoors.has(door)) {
                                    return;
                                }
                            } else {
                                const [door] = dimensionValues;
                                if (!validDoors.has(door)) {
                                    return;
                                }
                            }
                            onMutateBagValues({ bagType, rowId: newId, dimensionValues, quantity });
                        });
                    });

                    // Old to New
                    const bagTypesLabel = Object.values(AllocationBagType).filter((v) => typeof v === "string");
                    bagTypesLabel.forEach((field) => {
                        const bagType = AllocationBagType[field];
                        const cols = get(newItem, `[${field}].r`, []).map((r) => r.c);
                        cols.forEach((dimensionValues) => {
                            const quantity = dimensionValues.pop();
                            onMutateBagValues({ bagType, rowId: newId, dimensionValues, quantity });
                        });
                    });
                }

                // Copy Item
                newItem = allocationBagRef?.allocationBag ? pick(newItem, [ItemFields.Fields]) : newItem;
                onAddItem({
                    itemsId,
                    attributes: newItem,
                    addType: "paste",
                    index: items.size,
                    collectionId,
                });
            });

        if (collectionBrandId && mismatchedItems.length) {
            const message = formatMessage(
                "One or more items were not copied because it does not have the same brand as this assortment (|collectionBrandName|)",
                { collectionBrandName }
            );
            onSnackbarAdd({ message });
        }
    };

    private handleCloneItem = () => {
        const { itemForProductModal } = this.state;
        if (!itemForProductModal.itemId) {
            return;
        }
        const { items, itemsId, onAddItem, collectionId } = this.props;
        const itemToClone = items.find((item) => item.getIn(Item.FIELDS.ID) === itemForProductModal.itemId).toJS();
        const itemIndex = this.getRowIndexById(itemForProductModal.itemId, items);
        onAddItem({
            itemsId,
            attributes: {
                ...itemToClone,
                fields: {
                    ...itemToClone.fields,
                    id: { string_value: uuid() },
                },
            },
            addType: "clone",
            index: itemIndex || 0,
            collectionId,
        });
        this.setState({ isCloneConfirmationOpen: false });
        this.updateLocationQuery({
            itemId: undefined,
            productId: undefined,
            collectionId: undefined,
        });
    };

    private handleOpenDeleteConfirmation = (itemsId?, itemIds?) => {
        this.setState({
            isDeleteConfirmationOpen: true,
            itemsId,
            itemIds,
        });
    };

    private handleCloseDeleteConfirmation = () => {
        this.setState({ isDeleteConfirmationOpen: false });
    };

    private handleDeleteItems = debounce(
        () => {
            const { onDeleteItemsViaBulkItems, clearBagValues, itemsId, gridApi, collectionId } = this.props;
            const { itemIds } = this.state;
            this.handleCloseDeleteConfirmation();
            onDeleteItemsViaBulkItems(itemsId, itemIds, { gridApi });
            clearBagValues(itemIds, [AllocationBagType.allocations, AllocationBagType.bulks]);
            this.updateLocationQuery({
                itemId: undefined,
                productId: undefined,
                collectionId: undefined,
            });
        },
        1000,
        { leading: true, trailing: false }
    );

    private handleSearchChange = (searchText, force = false) => {
        // Short-circuit if there is no change to search text
        if (!force && searchText === this.state.searchText) {
            return;
        }

        if (force) {
            this.setState({ searchText, defaultSearchText: { value: searchText } });
        } else {
            this.setState({ searchText });
        }
        this.updateLocationQuery({ search: searchText });
    };

    private handleForceSearchChange = (searchText) => {
        this.setState({ searchText, defaultSearchText: { value: searchText } });
        this.updateLocationQuery({ search: searchText });
    };

    private handleToolbarFilterChange = ({ dimensionFilters, attributeFilters }: IFilters) => {
        // TODO: Apply attribute filters(NU-23130) AND persist the attributes filters(NU-29736)
        const { gridParams } = this.state;

        // Make a copy of the object so we don't want to mutate state object.
        let newFilter = JSON.parse(JSON.stringify((gridParams || {})[GridParamType.Filter] ?? {}));
        // Clear existing dimension filters. We will set it below.
        delete newFilter[GridParamType.DimensionFilters];
        delete newFilter[GridParamType.CustomAttributeFilter];

        newFilter = {
            ...newFilter,
            ...dimensionFiltersToAgGridFilters(dimensionFilters),
            ...attributeFiltersToAgGridFilters(attributeFilters),
        };

        this.handleGridParamsChange([
            {
                type: SaveStateToGridQueryParamsType.SetFilters,
                value: newFilter,
            },
        ]);
    };

    private calculateBulkTotals = (gridData?) => {
        if (this.props.isServerRollup) {
            return;
        }

        const { doors, dimensionsPanelApplied } = this.props;
        const { gridParams } = this.state;
        const tileViewData = gridData ?? this.props.tileViewData;

        const hasFilteredRows = this.props.tileGridData.data.size !== tileViewData.data.size;

        this.props.calculateBulkDoorTotals(
            this.props.tileGridData.data,
            getVisibleDoors(doors, dimensionsPanelApplied, { filter: undefined }),
            !hasFilteredRows
        );

        if (hasFilteredRows) {
            this.props.calculateBulkDoorTotalsFiltered(
                tileViewData.data,
                getVisibleDoors(doors, dimensionsPanelApplied, gridParams)
            );
        }
    };

    private onFirstDataRendered = (gridData) => {
        debounce(() => {
            this.calculateBulkTotals(gridData);
        }, SUCCESSIVE_CLICKS_TIME_MS);
    };

    private debouncedCalculateBothTotals = debounce((gridData, doors, dimensionsPanelApplied, gridParams) => {
        const hasFilteredRows = this.props.tileGridData.data.size !== gridData.data.size;

        this.props.calculateBulkDoorTotals(
            this.props.tileGridData.data,
            getVisibleDoors(doors, dimensionsPanelApplied, { filter: undefined }),
            !hasFilteredRows
        );

        if (hasFilteredRows) {
            this.props.calculateBulkDoorTotalsFiltered(
                gridData.data,
                getVisibleDoors(doors, dimensionsPanelApplied, gridParams)
            );
        }
    }, SUCCESSIVE_CLICKS_TIME_MS);

    private handleFilterChanged = (gridData) => {
        if (this.props.isServerRollup) {
            return;
        }
        const { doors, dimensionsPanelApplied } = this.props;
        const { gridParams } = this.state;
        this.debouncedCalculateBothTotals(gridData, doors, dimensionsPanelApplied, gridParams);
    };

    private handleClearSortAndFilters = () => {
        const { gridApi, columnApi } = this.props;
        const filteredChipData = this.state.savedViewData;
        columnApi?.setRowGroupColumns([]);
        columnApi?.setValueColumns([]);
        columnApi?.setPivotColumns(
            filteredChipData !== undefined ? filteredChipData?.grid_parameters?.pivot?.columns : undefined
        );

        if (this.state.pinnedRowColumnOrder) {
            this.setState({
                pinnedRowColumnOrder: undefined,
            });
        }

        this.handleGridParamsChange([
            {
                type: SaveStateToGridQueryParamsType.SetRowGroup,
                value: filteredChipData !== undefined ? filteredChipData?.grid_parameters?.row_groups : undefined,
            },
            {
                type: SaveStateToGridQueryParamsType.SetAggFunction,
                value:
                    filteredChipData !== undefined
                        ? filteredChipData?.grid_parameters?.aggregations.map((column) => {
                              return {
                                  column: column.column,
                                  aggFunc: column.function,
                              };
                          })
                        : undefined,
            },
            {
                type: SaveStateToGridQueryParamsType.SetPivot,
                value: undefined,
            },
            {
                type: SaveStateToGridQueryParamsType.SetSort,
                value: undefined,
            },
            {
                type: SaveStateToGridQueryParamsType.SetPinnedRowSort,
                value: undefined,
            },
            {
                type: SaveStateToGridQueryParamsType.SetFilters,
                value: undefined,
            },
        ]);
    };

    private handleToggleImageGroups = () => {
        const { location, router } = this.props;
        const { query } = location;
        if (query.panelView) {
            delete query.panelView;
            delete query.imagegroupid;
            router.push({
                pathname: location.pathname,
                query,
            });
        } else {
            this.updateLocationQuery({ cartView: View.Grid, panelView: Panel.ImageGroups });
        }
    };

    private handleViewErrorLog = () => {
        const { location, router } = this.props;
        const { query } = location;
        const { activeValidationMode } = this.state;
        if (this.state.statusExportItemPO) {
            const { item, po } = this.state.statusExportItemPO;
            let bothItemPo = false;
            if (item.active && po.active) {
                bothItemPo = true;
            }
            if (activeValidationMode === undefined) {
                this.props.fetchBothITemPO(bothItemPo);
            } else {
                this.props.fetchBothITemPO(false);
            }
        }

        router.push({
            pathname: location.pathname,
            query: {
                ...query,
                cartView: `${View.ValidationErrorLog}${activeValidationMode === ValidationModes.ValidatePOs ? 1 : 0}`,
                errorStatus: `${activeValidationMode === ValidationModes.ValidatePOs ? 1 : 0}`,
            },
        });
        this.handleValidationOutcomeClose();
    };
    private fetchItemPO = (status: any) => {
        this.setState({
            statusExportItemPO: status,
        });
    };

    private handleViewSwitch = (view) => {
        if (view) {
            const query: any = {
                cartView: view,
                validationRowId: undefined,
                errorStatus: undefined,
            };

            this.updateLocationQuery(query);
            if (view === View.Grid || view === View.Tile) {
                this.context.me.save(
                    {
                        last_assortment_view: view,
                    },
                    { patch: true }
                );
            }
        }
    };

    private handleShowLinkDialog = (itemId: string) => {
        this.setState({
            itemIdToLink: itemId,
        });
    };

    private handleLinkProductToItem = (attributes: any) => {
        const { itemsId, onAddItem, collectionId } = this.props;
        const { itemIdToLink: rowId } = this.state;
        const productId = attributes.fields.product_id.string_value;
        onAddItem({ attributes, itemsId, productId, rowId, collectionId });
        this.setState({ itemIdToLink: undefined });
    };

    private handleCancelLinkProductToItem = () => {
        this.setState({
            itemIdToLink: undefined,
            viewFullDetailProductId: "",
        });
    };

    private handleFilterAssortmentBySMU = (value: SMU_FILTER) => {
        this.setState({ filterAssortmentBySMU: value });
    };

    private debouncedUpdateLocationQuery = debounce((updatedGridParams: IGridParamsQueryParam, search?: string) => {
        this.updateLocationQuery({
            ...(search || search === "" ? { search } : {}),
            gridParams: JSON.stringify(updatedGridParams),
        });
    }, SUCCESSIVE_CLICKS_TIME_MS);

    private isDimensionSetRowGroup = (change: ValueOf<SaveStateToGridQueryParamsAction>) => {
        if (change.type === SaveStateToGridQueryParamsType.SetRowGroup) {
            return change.value?.some((column) => column.includes(ALLOCATION_DIMENSION_ID_PREFIX));
        }
        return false;
    };

    private shouldTurnOffDoorsGroupParams = (changes: ISaveStateToGridQueryParamsActions) => {
        if (changes instanceof Array) {
            return changes.some(this.isDimensionSetRowGroup);
        } else {
            return this.isDimensionSetRowGroup(changes);
        }
    };

    private handleGridParamsChange = (changes: ISaveStateToGridQueryParamsActions, search?: string) => {
        const gridParams = this.state.gridParams;
        const updatedGridParams = saveStateToGridQueryParams(changes, gridParams);

        // Turn off doors when dimension is set for rowGroup.
        if (this.shouldTurnOffDoorsGroupParams(changes)) {
            const updated = processActionsToDimensionParams(
                {
                    type: DimensionParamsActionType.ShowDoorsGroup,
                    value: false,
                },
                this.props.dimensionsPanelApplied
            );
            this.handleDimensionsStateChange(updated);
        }
        this.setState({ gridParams: updatedGridParams });
        this.setTileGridContext(
            this.computeTileGridContext(this.props, { ...this.state, gridParams: updatedGridParams })
        );
        this.debouncedUpdateLocationQuery(updatedGridParams, search);
    };

    private handleViewParamChange = (
        gridParams: IGridParamsQueryParam,
        updatedDimensionsState: DimensionsPanelReduxState,
        search: string,
        shouldLoadNewSavedView?: boolean
    ) => {
        const gridParamsFromLocation = getGridParamsFromLocation(this.props.location);
        const searchFromLocation = getSearchFromLocation(this.props.location);

        if (Object.keys(gridParamsFromLocation).length && !shouldLoadNewSavedView) {
            gridParams = {
                ...gridParams,
                ...gridParamsFromLocation,
            };
        }

        if (searchFromLocation && !shouldLoadNewSavedView) {
            search = searchFromLocation;
        }

        this.setState({
            searchText: search,
            defaultSearchText: {
                value: search,
            },
            gridParams,
        });
        this.handleDimensionsStateChange(updatedDimensionsState);
        this.updateLocationQuery({
            ...{ search },
            gridParams: JSON.stringify(gridParams),
        });

        // This will enforce re-rendering of columns downstream in Grid View component
        shouldLoadNewSavedView && this.handleResetColumns(true);
    };

    private handleClearViewParams = () => {
        this.setState({ gridParams: {}, searchText: undefined, defaultSearchText: { value: undefined } });
        // when we close the view than we need to clear the existing column label data.
        const { gridApi, columnApi } = this.props;
        columnApi?.setPivotColumns([]);
        this.updateLocationQuery({
            search: undefined,
            gridParams: undefined,
        });
        // This will enforce re-rendering of columns downstream in Grid View component
        this.handleResetColumns(true);
    };

    private handleToolPanelVisibleChanged = ({ api }) => {
        const toolpanelOpened = api.getOpenedToolPanel();
        const toolpanelIsBig = [ToolPanels.Dimension, ToolPanels.Shop].indexOf(toolpanelOpened) > -1;
        this.setState({ toolpanelIsBig: toolpanelIsBig, toolpanelOpened: toolpanelOpened });
    };

    private permissions = (
        mode: View,
        capabilities: nuorder.folder.v1.Capabilities
    ): { editable: boolean; userRole: string; accessPlan: boolean } => {
        let userRole;
        let editable = false;
        let accessPlan = false;
        const { isRollupMode, me } = this.props;
        const isHistoryMode = mode === View.History;
        const isDatalossMode = mode === View.Dataloss;
        if (capabilities) {
            editable =
                isRollupMode || isHistoryMode || (isDatalossMode && me.account_type === AccountType.NUORDER)
                    ? false
                    : capabilities.can_edit;
            accessPlan = capabilities?.collection_capabilities?.can_edit_plan || false;
        }
        return { editable, userRole, accessPlan };
    };

    private updateLocationQuery(updateData: any = {}) {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const latestQuery = Object.fromEntries(urlSearchParams.entries());

        const query = {
            ...latestQuery,
            ...updateData,
        };

        if (query.search === "") {
            delete query.search;
        }
        if (!query.gridParams || query.gridParams === "{}") {
            delete query.gridParams;
        }
        this.context.router.push({
            pathname: this.props.location.pathname,
            query,
        });
    }

    private getRowIndexById = memoizeOne((id, items): number => {
        // gets row index determined by collection not ag-grid
        if (!items) {
            return -1;
        }
        return items.findIndex((item) => item.getIn(Item.FIELDS.ID) === id);
    });

    private getRowAndRowIndex = memoizeOne((id, items) => {
        const rowIndex = this.getRowIndexById(id, items);
        let rowItem = null;
        if (rowIndex >= 0) {
            rowItem = items.get(rowIndex);
        }
        return {
            rowItem,
            rowIndex,
        };
    });

    private handleResetColumns = (reset) => {
        this.setState({
            isResetColumns: reset,
        });
    };

    private handlSetSuggestionData = (data: ISuggestionItem[], storeKey: string) => {
        this.props.onSetSuggestions(data, storeKey);
    };

    private handleSwitchingTileAndGrid = (switching: boolean) => {
        this.setState({
            switchingTile: switching,
        });
    };
}
